import { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, ToggleLeft, ToggleRight, X, Check, ShoppingCart, Building2, Wrench, Users, Calculator } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { useToast } from '../../../components/ui/toast';
import { api } from '../../../core/apiClient';
import SimpleCatalogTable from './SimpleCatalogTable';
import EstatsCrmTable from './EstatsCrmTable';
import AdminElementTree from './AdminElementTree';

// ─── Badge color palette ──────────────────────────────────────────────────────

const BADGE_COLORS: Record<string, { label: string; cls: string }> = {
  rose:   { label: 'Vermell',  cls: 'bg-rose-100 text-rose-700' },
  purple: { label: 'Morat',    cls: 'bg-purple-100 text-purple-700' },
  blue:   { label: 'Blau',     cls: 'bg-blue-100 text-blue-700' },
  green:  { label: 'Verd',     cls: 'bg-green-100 text-green-700' },
  amber:  { label: 'Ambre',    cls: 'bg-amber-100 text-amber-700' },
  teal:   { label: 'Teal',     cls: 'bg-teal-100 text-teal-700' },
  orange: { label: 'Taronja',  cls: 'bg-orange-100 text-orange-700' },
  slate:  { label: 'Gris',     cls: 'bg-slate-100 text-slate-700' },
};

// ─── Mòduls de les taules ─────────────────────────────────────────────────────
// Agrupa visualment les pestanyes per mòdul d'origen. Per afegir una taula
// nova a un mòdul, només cal afegir la seva entrada a TAB_DEFS.

const MODULS = ['Compres', 'CRM', 'Taller', 'RRHH', 'Comptabilitat'] as const;
type Modul = typeof MODULS[number];

const MODUL_ICON: Record<Modul, typeof ShoppingCart> = {
  Compres:       ShoppingCart,
  CRM:           Building2,
  Taller:        Wrench,
  RRHH:          Users,
  Comptabilitat: Calculator,
};

const MODUL_CARD_COLOR: Record<Modul, { border: string; bg: string; icon: string; iconBg: string }> = {
  Compres:       { border: 'border-blue-500',    bg: 'bg-blue-50/50',    icon: 'text-blue-500',    iconBg: 'bg-blue-500/10' },
  CRM:           { border: 'border-violet-500',  bg: 'bg-violet-50/50',  icon: 'text-violet-500',  iconBg: 'bg-violet-500/10' },
  Taller:        { border: 'border-orange-500',  bg: 'bg-orange-50/50',  icon: 'text-orange-500',  iconBg: 'bg-orange-500/10' },
  RRHH:          { border: 'border-emerald-500', bg: 'bg-emerald-50/50', icon: 'text-emerald-500', iconBg: 'bg-emerald-500/10' },
  Comptabilitat: { border: 'border-rose-500',    bg: 'bg-rose-50/50',    icon: 'text-rose-500',    iconBg: 'bg-rose-500/10' },
};

// Classes Tailwind estàtiques (cal que el JIT les vegi escrites literalment).
const GRID_COLS_CLASS: Record<number, string> = {
  1: 'grid gap-3 grid-cols-1',
  2: 'grid gap-3 grid-cols-2',
  3: 'grid gap-3 grid-cols-3',
  4: 'grid gap-3 grid-cols-4',
  5: 'grid gap-3 grid-cols-5',
};

const TAB_DEFS: { key: string; label: string; modul: Modul }[] = [
  { key: 'arbre-elements',      label: "Arbre d'Elements",     modul: 'Compres' },
  // "Tipus de Producte" / "Tipus d'Elements" / "Família d'Elements" amagades
  // (2026-10-21) — son UI duplicada sobre les mateixes taules que ja gestiona
  // "Arbre d'Elements" (mateixos endpoints: /catalog/composite-codes,
  // /catalog/element-types, /catalog/material-types). Es manté tot el codi
  // (blocs 'productes'/'elements'/'materials' mes avall) per si cal recuperar-
  // les; nomes es treuen del llistat de pestanyes visibles.
  { key: 'nodes',                label: "Tipus d'Escandall",    modul: 'Compres' },
  { key: 'activitats',          label: 'Activitat Proveïdors',  modul: 'Compres' },
  { key: 'naturalesa-client',   label: 'Naturalesa del Client', modul: 'CRM' },
  { key: 'clients',             label: 'Activitat del Client', modul: 'CRM' },
  { key: 'rol-client',          label: 'Rol del Client',        modul: 'CRM' },
  { key: 'estats-comercials',   label: 'Estat Comercial',       modul: 'CRM' },
  { key: 'paisos',              label: 'Països',               modul: 'CRM' },
  { key: 'formes-tramesa',      label: 'Formes de Tramesa',    modul: 'CRM' },
  { key: 'estats-crm',          label: 'Estats (EC / Pressupost)', modul: 'CRM' },
  { key: 'versions-pressupost', label: 'Versions Pressupost',  modul: 'CRM' },
  { key: 'idiomes-pressupost',  label: 'Idiomes Pressupost',   modul: 'CRM' },
  { key: 'metodes-pagament',    label: 'Mètodes de Pagament',  modul: 'Comptabilitat' },
  { key: 'tipus-projecte',      label: 'Tipus de Projecte',    modul: 'Taller' },
  { key: 'tipus-ot',            label: 'Tipus OT',             modul: 'Taller' },
  { key: 'grups-equipament',    label: 'Grup',                 modul: 'Taller' },
  { key: 'subgrups-equipament', label: 'Subgrup',              modul: 'Taller' },
  { key: 'ambits-equipament',   label: 'Àmbit',                modul: 'Taller' },
  { key: 'hores-personal',      label: 'Hores de Personal',    modul: 'RRHH' },
  { key: 'relacio-laboral',     label: 'Relació Laboral',      modul: 'RRHH' },
  { key: 'tipus-contracte',     label: 'Tipus de Contracte',   modul: 'RRHH' },
  { key: 'departament',         label: 'Departament',          modul: 'RRHH' },
  { key: 'carrec',              label: 'Càrrec',               modul: 'RRHH' },
  { key: 'talla-pantalons',     label: 'Talla de Pantalons',   modul: 'RRHH' },
  { key: 'talla-camisa',        label: 'Talla de Camisa',      modul: 'RRHH' },
];

// ─── Types ────────────────────────────────────────────────────────────────────

interface ElementType {
  id: number;
  code: string;
  name: string;
  description?: string;
  isActive: number;
}

interface ClientType {
  id: number;
  code: string;
  name: string;
  description?: string;
  naturalesaClientId?: number | null;
  naturalesaClient?: { id: number; name: string } | null;
  isActive: number;
}

interface NaturalesaClient {
  id: number;
  code: string;
  name: string;
  description?: string;
  isActive: number;
}

interface RolClient {
  id: number;
  code: string;
  name: string;
  description?: string;
  isActive: number;
}

interface EstatComercial {
  id: number;
  code: string;
  name: string;
  description?: string;
  isActive: number;
}

interface NodeType {
  code: string;
  name: string;
  description?: string;
  badgeColor: string;
  isActive: number;
}

interface MaterialType {
  id: number;
  code: string;
  name: string;
  description?: string;
  parentId?: number | null;
  parent?: { id: number; name: string } | null;
  elementTypeId?: number | null;
  elementType?: { id: number; name: string } | null;
  isActive: number;
}

interface Activitat {
  id: number;
  code?: string | null;
  name: string;
  isActive: number;
}

interface Pais {
  id: number;
  name: string;
  code: string | null;
  isActive: number;
}

interface MetodePagament {
  id: number;
  name: string;
  isActive: number;
}

interface FormaTramesa {
  id: number;
  name: string;
  isActive: number;
}

interface TipusProjecte {
  id: number;
  code: string;
  name: string;
  isActive: number;
}

interface WorkOrderTypeAdmin {
  id: number;
  code: string;
  name: string;
  description?: string;
  isActive: number;
}

interface GrupEquipament {
  id: number;
  code: string;
  name: string;
  isActive: number;
}

interface SubgrupEquipament {
  id: number;
  code: string;
  name: string;
  isActive: number;
}

interface AmbitEquipament {
  id: number;
  code: string;
  name: string;
  isActive: number;
}

interface GrupExpedientComercial {
  id: number;
  code: string;
  name: string;
  isActive: number;
}

interface HoraPersonal {
  id: number;
  code: string;
  name: string;
  hourlyRate: number;
  isActive: number;
}

interface CompositeCode {
  id: number;
  code: string;
  name: string;
  familia?: string;
  subfamilia?: string;
  description?: string;
  marginPercentage?: number;
  isActive: number;
  brandId?: string;
  brandName?: string;
  brandCode?: string;
}

interface Brand {
  id: string;
  code: string;
  name: string;
}

// ─── Empty forms ─────────────────────────────────────────────────────────────

const emptyAC = () => ({ code: '', name: '' });
const emptyPS = () => ({ name: '', code: '' });
const emptyMP = () => ({ name: '' });
const emptyFT = () => ({ name: '' });
const emptyTP = () => ({ code: '', name: '' });
const emptyWOT = () => ({ code: '', name: '', description: '' });
const emptyGE = () => ({ code: '', name: '' });
const emptySGE = () => ({ code: '', name: '' });
const emptyAE = () => ({ code: '', name: '' });
const emptyGEC = () => ({ code: '', name: '' });
const emptyHP = () => ({ code: '', name: '', hourlyRate: '' });
const emptyCT = () => ({ code: '', name: '', description: '', naturalesaClientId: '' });
const emptyNC = () => ({ code: '', name: '', description: '' });
const emptyRC = () => ({ code: '', name: '', description: '' });
const emptyES = () => ({ code: '', name: '', description: '' });
const emptyET = () => ({ code: '', name: '', description: '' });
const emptyMT = () => ({ code: '', name: '', description: '', parentId: '', elementTypeId: '' });
const emptyNT = () => ({ code: '', name: '', description: '', badgeColor: 'slate' });
// familia/subfamilia ja no s'editen aquí — substituïdes per la jerarquia
// real a "Arbre d'Elements" (Subfamília/Producte/Model).
const emptyCC = () => ({ code: '', name: '', description: '', marginPercentage: '', brandId: '' });

// ─── Inline row editor ────────────────────────────────────────────────────────

function InlineInput({ value, onChange, placeholder, className = '' }: {
  value: string; onChange: (v: string) => void; placeholder?: string; className?: string;
}) {
  return (
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`h-7 text-xs px-2 ${className}`}
    />
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function AdminTypes() {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<'arbre-elements' | 'elements' | 'materials' | 'nodes' | 'clients' | 'productes' | 'activitats' | 'paisos' | 'metodes-pagament' | 'formes-tramesa' | 'estats-crm' | 'versions-pressupost' | 'idiomes-pressupost' | 'tipus-projecte' | 'tipus-ot' | 'grups-equipament' | 'subgrups-equipament' | 'ambits-equipament' | 'grups-ec' | 'naturalesa-client' | 'rol-client' | 'estats-comercials' | 'hores-personal' | 'relacio-laboral' | 'tipus-contracte' | 'departament' | 'carrec' | 'talla-pantalons' | 'talla-camisa'>('arbre-elements');
  const [activeModul, setActiveModul] = useState<Modul>('Compres');

  // Activitats state
  const [activitats, setActivitats] = useState<Activitat[]>([]);
  const [acLoading, setAcLoading] = useState(true);
  const [acError, setAcError] = useState<string | null>(null);
  const [acEditId, setAcEditId] = useState<number | null>(null);
  const [acEditForm, setAcEditForm] = useState(emptyAC());
  const [acNewForm, setAcNewForm] = useState(emptyAC());
  const [acAdding, setAcAdding] = useState(false);
  const [acDragId, setAcDragId] = useState<number | null>(null);
  const [acDragOverId, setAcDragOverId] = useState<number | null>(null);

  // Paisos state
  const [paisos, setPaisos] = useState<Pais[]>([]);
  const [psLoading, setPsLoading] = useState(true);
  const [psError, setPsError] = useState<string | null>(null);
  const [psEditId, setPsEditId] = useState<number | null>(null);
  const [psEditForm, setPsEditForm] = useState(emptyPS());
  const [psNewForm, setPsNewForm] = useState(emptyPS());
  const [psAdding, setPsAdding] = useState(false);
  const [psDragId, setPsDragId] = useState<number | null>(null);
  const [psDragOverId, setPsDragOverId] = useState<number | null>(null);

  // Metodes Pagament state
  const [metodesPagament, setMetodesPagament] = useState<MetodePagament[]>([]);
  const [mpLoading, setMpLoading] = useState(true);
  const [mpError, setMpError] = useState<string | null>(null);
  const [mpEditId, setMpEditId] = useState<number | null>(null);
  const [mpEditForm, setMpEditForm] = useState(emptyMP());
  const [mpNewForm, setMpNewForm] = useState(emptyMP());
  const [mpAdding, setMpAdding] = useState(false);

  // Formes Tramesa state
  const [formesTramesa, setFormesTramesa] = useState<FormaTramesa[]>([]);
  const [ftLoading, setFtLoading] = useState(true);
  const [ftError, setFtError] = useState<string | null>(null);
  const [ftEditId, setFtEditId] = useState<number | null>(null);
  const [ftEditForm, setFtEditForm] = useState(emptyFT());
  const [ftNewForm, setFtNewForm] = useState(emptyFT());
  const [ftAdding, setFtAdding] = useState(false);
  const [ftDragId, setFtDragId] = useState<number | null>(null);
  const [ftDragOverId, setFtDragOverId] = useState<number | null>(null);

  // Tipus Projecte state
  const [tipusProjecte, setTipusProjecte] = useState<TipusProjecte[]>([]);
  const [tpLoading, setTpLoading] = useState(true);
  const [tpError, setTpError] = useState<string | null>(null);
  const [tpEditId, setTpEditId] = useState<number | null>(null);
  const [tpEditForm, setTpEditForm] = useState(emptyTP());
  const [tpNewForm, setTpNewForm] = useState(emptyTP());
  const [tpAdding, setTpAdding] = useState(false);
  const [tpDragId, setTpDragId] = useState<number | null>(null);
  const [tpDragOverId, setTpDragOverId] = useState<number | null>(null);

  const [workOrderTypes, setWorkOrderTypes] = useState<WorkOrderTypeAdmin[]>([]);
  const [wotLoading, setWotLoading] = useState(true);
  const [wotError, setWotError] = useState<string | null>(null);
  const [wotEditId, setWotEditId] = useState<number | null>(null);
  const [wotEditForm, setWotEditForm] = useState(emptyWOT());
  const [wotNewForm, setWotNewForm] = useState(emptyWOT());
  const [wotAdding, setWotAdding] = useState(false);
  const [wotDragId, setWotDragId] = useState<number | null>(null);
  const [wotDragOverId, setWotDragOverId] = useState<number | null>(null);

  // Grup Equipament state
  const [grupsEquipament, setGrupsEquipament] = useState<GrupEquipament[]>([]);
  const [geLoading, setGeLoading] = useState(true);
  const [geError, setGeError] = useState<string | null>(null);
  const [geEditId, setGeEditId] = useState<number | null>(null);
  const [geEditForm, setGeEditForm] = useState(emptyGE());
  const [geNewForm, setGeNewForm] = useState(emptyGE());
  const [geAdding, setGeAdding] = useState(false);

  // Subgrup Equipament state
  const [subgrupsEquipament, setSubgrupsEquipament] = useState<SubgrupEquipament[]>([]);
  const [sgeLoading, setSgeLoading] = useState(true);
  const [sgeError, setSgeError] = useState<string | null>(null);
  const [sgeEditId, setSgeEditId] = useState<number | null>(null);
  const [sgeEditForm, setSgeEditForm] = useState(emptySGE());
  const [sgeNewForm, setSgeNewForm] = useState(emptySGE());
  const [sgeAdding, setSgeAdding] = useState(false);

  // Àmbit Equipament state
  const [ambitsEquipament, setAmbitsEquipament] = useState<AmbitEquipament[]>([]);
  const [aeLoading, setAeLoading] = useState(true);
  const [aeError, setAeError] = useState<string | null>(null);
  const [aeEditId, setAeEditId] = useState<number | null>(null);
  const [aeEditForm, setAeEditForm] = useState(emptyAE());
  const [aeNewForm, setAeNewForm] = useState(emptyAE());
  const [aeAdding, setAeAdding] = useState(false);

  // Grups Expedient Comercial state
  const [grupsEC, setGrupsEC] = useState<GrupExpedientComercial[]>([]);
  const [gecLoading, setGecLoading] = useState(true);
  const [gecError, setGecError] = useState<string | null>(null);
  const [gecEditId, setGecEditId] = useState<number | null>(null);
  const [gecEditForm, setGecEditForm] = useState(emptyGEC());
  const [gecNewForm, setGecNewForm] = useState(emptyGEC());
  const [gecAdding, setGecAdding] = useState(false);

  const [hoursPersonal, setHoursPersonal] = useState<HoraPersonal[]>([]);
  const [hpLoading, setHpLoading] = useState(true);
  const [hpError, setHpError] = useState<string | null>(null);
  const [hpEditId, setHpEditId] = useState<number | null>(null);
  const [hpEditForm, setHpEditForm] = useState(emptyHP());
  const [hpNewForm, setHpNewForm] = useState(emptyHP());
  const [hpAdding, setHpAdding] = useState(false);

  // Client Types state
  const [clientTypes, setClientTypes] = useState<ClientType[]>([]);
  const [ctLoading, setCtLoading] = useState(true);
  const [ctError, setCtError] = useState<string | null>(null);
  const [ctEditId, setCtEditId] = useState<number | null>(null);
  const [ctEditForm, setCtEditForm] = useState(emptyCT());
  const [ctNewForm, setCtNewForm] = useState(emptyCT());
  const [ctAdding, setCtAdding] = useState(false);
  const [ctDragId, setCtDragId] = useState<number | null>(null);
  const [ctDragOverId, setCtDragOverId] = useState<number | null>(null);

  // Naturalesa Client state
  const [naturalesaClient, setNaturalesaClient] = useState<NaturalesaClient[]>([]);
  const [ncLoading, setNcLoading] = useState(true);
  const [ncError, setNcError] = useState<string | null>(null);
  const [ncEditId, setNcEditId] = useState<number | null>(null);
  const [ncEditForm, setNcEditForm] = useState(emptyNC());
  const [ncNewForm, setNcNewForm] = useState(emptyNC());
  const [ncAdding, setNcAdding] = useState(false);
  const [ncDragId, setNcDragId] = useState<number | null>(null);
  const [ncDragOverId, setNcDragOverId] = useState<number | null>(null);

  // Rol Client state
  const [rolClient, setRolClient] = useState<RolClient[]>([]);
  const [rcLoading, setRcLoading] = useState(true);
  const [rcError, setRcError] = useState<string | null>(null);
  const [rcEditId, setRcEditId] = useState<number | null>(null);
  const [rcEditForm, setRcEditForm] = useState(emptyRC());
  const [rcNewForm, setRcNewForm] = useState(emptyRC());
  const [rcAdding, setRcAdding] = useState(false);
  const [rcDragId, setRcDragId] = useState<number | null>(null);
  const [rcDragOverId, setRcDragOverId] = useState<number | null>(null);

  // Estat Comercial state
  const [estatsComercials, setEstatsComercials] = useState<EstatComercial[]>([]);
  const [esLoading, setEsLoading] = useState(true);
  const [esError, setEsError] = useState<string | null>(null);
  const [esEditId, setEsEditId] = useState<number | null>(null);
  const [esEditForm, setEsEditForm] = useState(emptyES());
  const [esNewForm, setEsNewForm] = useState(emptyES());
  const [esAdding, setEsAdding] = useState(false);
  const [esDragId, setEsDragId] = useState<number | null>(null);
  const [esDragOverId, setEsDragOverId] = useState<number | null>(null);

  // Element Types state
  const [elementTypes, setElementTypes] = useState<ElementType[]>([]);
  const [etLoading, setEtLoading] = useState(true);
  const [etError, setEtError] = useState<string | null>(null);
  const [etEditId, setEtEditId] = useState<number | null>(null);
  const [etEditForm, setEtEditForm] = useState(emptyET());
  const [etNewForm, setEtNewForm] = useState(emptyET());
  const [etAdding, setEtAdding] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  // Node Types state
  const [nodeTypes, setNodeTypes] = useState<NodeType[]>([]);
  const [ntLoading, setNtLoading] = useState(true);
  const [ntError, setNtError] = useState<string | null>(null);
  const [ntEditCode, setNtEditCode] = useState<string | null>(null);
  const [ntEditForm, setNtEditForm] = useState(emptyNT());
  const [ntNewForm, setNtNewForm] = useState(emptyNT());
  const [ntAdding, setNtAdding] = useState(false);

  // Composite Codes state
  const [compositeCodes, setCompositeCodes] = useState<CompositeCode[]>([]);
  const [ccLoading, setCcLoading] = useState(true);
  const [ccError, setCcError] = useState<string | null>(null);
  const [ccEditId, setCcEditId] = useState<number | null>(null);
  const [ccEditForm, setCcEditForm] = useState(emptyCC());
  const [ccNewForm, setCcNewForm] = useState(emptyCC());
  const [ccAdding, setCcAdding] = useState(false);
  const [ccDragId, setCcDragId] = useState<number | null>(null);
  const [ccDragOverId, setCcDragOverId] = useState<number | null>(null);
  const [brands, setBrands] = useState<Brand[]>([]);

  // Material Types state
  const [materialTypes, setMaterialTypes] = useState<MaterialType[]>([]);
  const [mtLoading, setMtLoading] = useState(true);
  const [mtError, setMtError] = useState<string | null>(null);
  const [mtEditId, setMtEditId] = useState<number | null>(null);
  const [mtEditForm, setMtEditForm] = useState(emptyMT());
  const [mtNewForm, setMtNewForm] = useState(emptyMT());
  const [mtAdding, setMtAdding] = useState(false);
  const [mtDragId, setMtDragId] = useState<number | null>(null);
  const [mtDragOverId, setMtDragOverId] = useState<number | null>(null);

  // ── Fetchers ─────────────────────────────────────────────────────────────────

  const fetchActivitats = useCallback(async () => {
    setAcLoading(true); setAcError(null);
    try {
      const res = await api.get<{ success: boolean; data: Activitat[] }>('/admin/activitats');
      setActivitats(res.data);
    } catch { setAcError("Error en carregar les activitats"); }
    finally { setAcLoading(false); }
  }, []);

  const fetchPaisos = useCallback(async () => {
    setPsLoading(true); setPsError(null);
    try {
      const res = await api.get<{ success: boolean; data: Pais[] }>('/admin/paisos');
      setPaisos(res.data);
    } catch { setPsError("Error en carregar els països"); }
    finally { setPsLoading(false); }
  }, []);

  const fetchMetodesPagament = useCallback(async () => {
    setMpLoading(true); setMpError(null);
    try {
      const res = await api.get<{ success: boolean; data: MetodePagament[] }>('/admin/metodes-pagament');
      setMetodesPagament(res.data);
    } catch { setMpError("Error en carregar els mètodes de pagament"); }
    finally { setMpLoading(false); }
  }, []);

  const fetchFormesTramesa = useCallback(async () => {
    setFtLoading(true); setFtError(null);
    try {
      const res = await api.get<{ success: boolean; data: FormaTramesa[] }>('/admin/formes-tramesa');
      setFormesTramesa(res.data);
    } catch { setFtError("Error en carregar les formes de tramesa"); }
    finally { setFtLoading(false); }
  }, []);

  const fetchCompositeCodes = useCallback(async () => {
    setCcLoading(true);
    setCcError(null);
    try {
      const res = await api.get<{ success: boolean; data: CompositeCode[] }>('/catalog/composite-codes');
      setCompositeCodes(res.data);
    } catch { setCcError('Error en carregar els tipus de producte'); }
    finally { setCcLoading(false); }
  }, []);

  const fetchBrands = useCallback(async () => {
    try {
      const res = await api.get<{ success: boolean; data: Brand[] }>('/brands');
      setBrands(res.data);
    } catch { /* la marca és opcional, no cal bloquejar la pestanya */ }
  }, []);

  const fetchClientTypes = useCallback(async () => {
    setCtLoading(true);
    setCtError(null);
    try {
      const res = await api.get<{ success: boolean; data: ClientType[] }>('/catalog/client-types');
      setClientTypes(res.data);
    } catch { setCtError('Error en carregar les activitats de client'); }
    finally { setCtLoading(false); }
  }, []);

  const fetchNaturalesaClient = useCallback(async () => {
    setNcLoading(true); setNcError(null);
    try {
      const res = await api.get<{ success: boolean; data: NaturalesaClient[] }>('/catalog/naturalesa-client');
      setNaturalesaClient(res.data);
    } catch { setNcError('Error en carregar la naturalesa de client'); }
    finally { setNcLoading(false); }
  }, []);

  const fetchRolClient = useCallback(async () => {
    setRcLoading(true); setRcError(null);
    try {
      const res = await api.get<{ success: boolean; data: RolClient[] }>('/catalog/rol-client');
      setRolClient(res.data);
    } catch { setRcError('Error en carregar el rol de client'); }
    finally { setRcLoading(false); }
  }, []);

  const fetchEstatsComercials = useCallback(async () => {
    setEsLoading(true); setEsError(null);
    try {
      const res = await api.get<{ success: boolean; data: EstatComercial[] }>('/catalog/estats-comercials');
      setEstatsComercials(res.data);
    } catch { setEsError("Error en carregar els estats comercials"); }
    finally { setEsLoading(false); }
  }, []);

  const fetchElementTypes = useCallback(async () => {
    setEtLoading(true);
    setEtError(null);
    try {
      const res = await api.get<{ success: boolean; data: ElementType[] }>('/catalog/element-types');
      setElementTypes(res.data);
    } catch { setEtError('Error en carregar els tipus d\'element'); }
    finally { setEtLoading(false); }
  }, []);

  const fetchMaterialTypes = useCallback(async () => {
    setMtLoading(true);
    setMtError(null);
    try {
      const res = await api.get<{ success: boolean; data: MaterialType[] }>('/catalog/material-types');
      setMaterialTypes(res.data);
    } catch { setMtError('Error en carregar els tipus de material'); }
    finally { setMtLoading(false); }
  }, []);

  const fetchNodeTypes = useCallback(async () => {
    setNtLoading(true);
    setNtError(null);
    try {
      const res = await api.get<{ success: boolean; data: NodeType[] }>('/catalog/node-types');
      setNodeTypes(res.data);
    } catch { setNtError('Error en carregar els tipus d\'escandall'); }
    finally { setNtLoading(false); }
  }, []);

  useEffect(() => { fetchActivitats(); }, [fetchActivitats]);
  useEffect(() => { fetchPaisos(); }, [fetchPaisos]);
  useEffect(() => { fetchMetodesPagament(); }, [fetchMetodesPagament]);
  useEffect(() => { fetchFormesTramesa(); }, [fetchFormesTramesa]);

  const fetchTipusProjecte = useCallback(async () => {
    setTpLoading(true); setTpError(null);
    try {
      const res = await api.get<{ success: boolean; data: TipusProjecte[] }>('/admin/tipus-projecte');
      setTipusProjecte(res.data);
    } catch { setTpError("Error en carregar els tipus de projecte"); }
    finally { setTpLoading(false); }
  }, []);

  const fetchWorkOrderTypes = useCallback(async () => {
    setWotLoading(true); setWotError(null);
    try {
      const res = await api.get<{ success: boolean; data: WorkOrderTypeAdmin[] }>('/admin/work-order-types');
      setWorkOrderTypes(res.data);
    } catch { setWotError("Error en carregar els tipus d'OT"); }
    finally { setWotLoading(false); }
  }, []);

  const fetchGrupsEquipament = useCallback(async () => {
    setGeLoading(true); setGeError(null);
    try {
      const res = await api.get<{ success: boolean; data: GrupEquipament[] }>('/catalog/grups-equipament');
      setGrupsEquipament(res.data);
    } catch { setGeError('Error en carregar els grups'); }
    finally { setGeLoading(false); }
  }, []);

  const fetchSubgrupsEquipament = useCallback(async () => {
    setSgeLoading(true); setSgeError(null);
    try {
      const res = await api.get<{ success: boolean; data: SubgrupEquipament[] }>('/catalog/subgrups-equipament');
      setSubgrupsEquipament(res.data);
    } catch { setSgeError('Error en carregar els subgrups'); }
    finally { setSgeLoading(false); }
  }, []);

  const fetchAmbitsEquipament = useCallback(async () => {
    setAeLoading(true); setAeError(null);
    try {
      const res = await api.get<{ success: boolean; data: AmbitEquipament[] }>('/catalog/ambits-equipament');
      setAmbitsEquipament(res.data);
    } catch { setAeError('Error en carregar els àmbits'); }
    finally { setAeLoading(false); }
  }, []);

  const fetchGrupsEC = useCallback(async () => {
    setGecLoading(true); setGecError(null);
    try {
      const res = await api.get<{ success: boolean; data: GrupExpedientComercial[] }>('/admin/grups-expedient-comercial');
      setGrupsEC(res.data);
    } catch { setGecError('Error en carregar els grups'); }
    finally { setGecLoading(false); }
  }, []);

  const fetchHoresPersonal = useCallback(async () => {
    setHpLoading(true); setHpError(null);
    try {
      const res = await api.get<{ success: boolean; data: HoraPersonal[] }>('/admin/hores-personal');
      setHoursPersonal(res.data);
    } catch { setHpError('Error en carregar les hores de personal'); }
    finally { setHpLoading(false); }
  }, []);

  useEffect(() => { fetchTipusProjecte(); }, [fetchTipusProjecte]);
  useEffect(() => { fetchWorkOrderTypes(); }, [fetchWorkOrderTypes]);
  useEffect(() => { fetchGrupsEquipament(); }, [fetchGrupsEquipament]);
  useEffect(() => { fetchSubgrupsEquipament(); }, [fetchSubgrupsEquipament]);
  useEffect(() => { fetchAmbitsEquipament(); }, [fetchAmbitsEquipament]);
  useEffect(() => { fetchGrupsEC(); }, [fetchGrupsEC]);
  useEffect(() => { fetchHoresPersonal(); }, [fetchHoresPersonal]);
  useEffect(() => { fetchCompositeCodes(); }, [fetchCompositeCodes]);
  useEffect(() => { fetchBrands(); }, [fetchBrands]);
  useEffect(() => { fetchClientTypes(); }, [fetchClientTypes]);
  useEffect(() => { fetchNaturalesaClient(); }, [fetchNaturalesaClient]);
  useEffect(() => { fetchRolClient(); }, [fetchRolClient]);
  useEffect(() => { fetchEstatsComercials(); }, [fetchEstatsComercials]);
  useEffect(() => { fetchElementTypes(); }, [fetchElementTypes]);
  useEffect(() => { fetchMaterialTypes(); }, [fetchMaterialTypes]);
  useEffect(() => { fetchNodeTypes(); }, [fetchNodeTypes]);

  // ── Activitats CRUD ───────────────────────────────────────────────────────────

  const acSaveNew = async () => {
    if (!acNewForm.name.trim()) { setAcError('El nom és obligatori'); return; }
    try {
      await api.post('/admin/activitats', acNewForm);
      setAcNewForm(emptyAC()); setAcAdding(false); fetchActivitats();
    } catch (e: any) { setAcError(e?.response?.data?.message || 'Error en crear'); }
  };

  const acStartEdit = (item: Activitat) => {
    setAcEditId(item.id); setAcEditForm({ code: item.code || '', name: item.name });
  };

  const acSaveEdit = async () => {
    if (!acEditId) return;
    try {
      await api.put(`/admin/activitats/${acEditId}`, acEditForm);
      setAcEditId(null); fetchActivitats();
    } catch (e: any) { setAcError(e?.response?.data?.message || 'Error en desar'); }
  };

  const acToggleActive = async (item: Activitat) => {
    try {
      await api.put(`/admin/activitats/${item.id}`, { isActive: item.isActive ? 0 : 1 });
      fetchActivitats();
    } catch { setAcError('Error en canviar estat'); }
  };

  const acDragStart = (id: number) => setAcDragId(id);
  const acDragOver = (e: React.DragEvent, id: number) => { e.preventDefault(); setAcDragOverId(id); };
  const acDragEnd = () => { setAcDragId(null); setAcDragOverId(null); };
  const acDrop = async (targetId: number) => {
    if (!acDragId || acDragId === targetId) { acDragEnd(); return; }
    const reordered = [...activitats];
    const fromIdx = reordered.findIndex(a => a.id === acDragId);
    const toIdx = reordered.findIndex(a => a.id === targetId);
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    setActivitats(reordered);
    acDragEnd();
    try {
      await api.put('/admin/activitats/reorder', { ids: reordered.map(a => a.id) });
    } catch { setAcError("Error en desar l'ordre"); fetchActivitats(); }
  };

  const acDelete = async (item: Activitat) => {
    if (!confirm(`Eliminar l'activitat "${item.name}"?`)) return;
    try {
      const r = await api.delete<{ message?: string }>(`/admin/activitats/${item.id}`);
      showToast(r.message || 'Eliminat correctament', 'success');
      fetchActivitats();
    } catch (e: any) { const msg = e?.response?.data?.message || 'Error en eliminar'; setAcError(msg); showToast(msg, 'error'); }
  };

  // ── Paisos CRUD ───────────────────────────────────────────────────────────────

  const psSaveNew = async () => {
    if (!psNewForm.name.trim()) { setPsError('El nom és obligatori'); return; }
    try {
      await api.post('/admin/paisos', psNewForm);
      setPsNewForm(emptyPS()); setPsAdding(false); fetchPaisos();
    } catch (e: any) { setPsError(e?.response?.data?.message || 'Error en crear'); }
  };

  const psStartEdit = (item: Pais) => {
    setPsEditId(item.id); setPsEditForm({ name: item.name, code: item.code || '' });
  };

  const psSaveEdit = async () => {
    if (!psEditId) return;
    try {
      await api.put(`/admin/paisos/${psEditId}`, psEditForm);
      setPsEditId(null); fetchPaisos();
    } catch (e: any) { setPsError(e?.response?.data?.message || 'Error en desar'); }
  };

  const psToggleActive = async (item: Pais) => {
    try {
      await api.put(`/admin/paisos/${item.id}`, { isActive: item.isActive ? 0 : 1 });
      fetchPaisos();
    } catch { setPsError('Error en canviar estat'); }
  };

  const psDelete = async (item: Pais) => {
    if (!confirm(`Eliminar el país "${item.name}"?`)) return;
    try {
      const r = await api.delete<{ message?: string }>(`/admin/paisos/${item.id}`);
      showToast(r.message || 'Eliminat correctament', 'success');
      fetchPaisos();
    } catch (e: any) { const msg = e?.response?.data?.message || 'Error en eliminar'; setPsError(msg); showToast(msg, 'error'); }
  };

  const psDragStart = (id: number) => setPsDragId(id);
  const psDragOver = (e: React.DragEvent, id: number) => { e.preventDefault(); setPsDragOverId(id); };
  const psDragEnd = () => { setPsDragId(null); setPsDragOverId(null); };
  const psDrop = async (targetId: number) => {
    if (!psDragId || psDragId === targetId) { psDragEnd(); return; }
    const reordered = [...paisos];
    const from = reordered.findIndex(c => c.id === psDragId);
    const to = reordered.findIndex(c => c.id === targetId);
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    setPaisos(reordered);
    psDragEnd();
    try { await api.put('/admin/paisos/reorder', { ids: reordered.map(c => c.id) }); }
    catch { setPsError("Error en desar l'ordre"); fetchPaisos(); }
  };

  // ── Metodes Pagament CRUD ─────────────────────────────────────────────────────

  const mpSaveNew = async () => {
    if (!mpNewForm.name.trim()) { setMpError('El nom és obligatori'); return; }
    try {
      await api.post('/admin/metodes-pagament', mpNewForm);
      setMpNewForm(emptyMP()); setMpAdding(false); fetchMetodesPagament();
    } catch (e: any) { setMpError(e?.response?.data?.message || 'Error en crear'); }
  };

  const mpStartEdit = (item: MetodePagament) => {
    setMpEditId(item.id); setMpEditForm({ name: item.name });
  };

  const mpSaveEdit = async () => {
    if (!mpEditId) return;
    try {
      await api.put(`/admin/metodes-pagament/${mpEditId}`, mpEditForm);
      setMpEditId(null); fetchMetodesPagament();
    } catch (e: any) { setMpError(e?.response?.data?.message || 'Error en desar'); }
  };

  const mpToggleActive = async (item: MetodePagament) => {
    try {
      await api.put(`/admin/metodes-pagament/${item.id}`, { isActive: item.isActive ? 0 : 1 });
      fetchMetodesPagament();
    } catch { setMpError('Error en canviar estat'); }
  };

  const mpDelete = async (item: MetodePagament) => {
    if (!confirm(`Eliminar el mètode de pagament "${item.name}"?`)) return;
    try {
      const r = await api.delete<{ message?: string }>(`/admin/metodes-pagament/${item.id}`);
      showToast(r.message || 'Eliminat correctament', 'success');
      fetchMetodesPagament();
    } catch (e: any) { const msg = e?.response?.data?.message || 'Error en eliminar'; setMpError(msg); showToast(msg, 'error'); }
  };

  // ── Formes Tramesa CRUD ───────────────────────────────────────────────────────

  const ftSaveNew = async () => {
    if (!ftNewForm.name.trim()) { setFtError('El nom és obligatori'); return; }
    try {
      await api.post('/admin/formes-tramesa', ftNewForm);
      setFtNewForm(emptyFT()); setFtAdding(false); fetchFormesTramesa();
    } catch (e: any) { setFtError(e?.response?.data?.message || 'Error en crear'); }
  };

  const ftStartEdit = (item: FormaTramesa) => {
    setFtEditId(item.id); setFtEditForm({ name: item.name });
  };

  const ftSaveEdit = async () => {
    if (!ftEditId) return;
    try {
      await api.put(`/admin/formes-tramesa/${ftEditId}`, ftEditForm);
      setFtEditId(null); fetchFormesTramesa();
    } catch (e: any) { setFtError(e?.response?.data?.message || 'Error en desar'); }
  };

  const ftToggleActive = async (item: FormaTramesa) => {
    try {
      await api.put(`/admin/formes-tramesa/${item.id}`, { isActive: item.isActive ? 0 : 1 });
      fetchFormesTramesa();
    } catch { setFtError('Error en canviar estat'); }
  };

  const ftDelete = async (item: FormaTramesa) => {
    if (!confirm(`Eliminar la forma de tramesa "${item.name}"?`)) return;
    try {
      const r = await api.delete<{ message?: string }>(`/admin/formes-tramesa/${item.id}`);
      showToast(r.message || 'Eliminat correctament', 'success');
      fetchFormesTramesa();
    } catch (e: any) { const msg = e?.response?.data?.message || 'Error en eliminar'; setFtError(msg); showToast(msg, 'error'); }
  };

  const ftDragStart = (id: number) => setFtDragId(id);
  const ftDragOver = (e: React.DragEvent, id: number) => { e.preventDefault(); setFtDragOverId(id); };
  const ftDragEnd = () => { setFtDragId(null); setFtDragOverId(null); };
  const ftDrop = async (targetId: number) => {
    if (!ftDragId || ftDragId === targetId) { ftDragEnd(); return; }
    const reordered = [...formesTramesa];
    const from = reordered.findIndex(c => c.id === ftDragId);
    const to = reordered.findIndex(c => c.id === targetId);
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    setFormesTramesa(reordered);
    ftDragEnd();
    try { await api.put('/admin/formes-tramesa/reorder', { ids: reordered.map(c => c.id) }); }
    catch { setFtError("Error en desar l'ordre"); fetchFormesTramesa(); }
  };

  // ── Tipus Projecte CRUD ───────────────────────────────────────────────────────

  const tpSaveNew = async () => {
    if (!tpNewForm.code.trim() || !tpNewForm.name.trim()) { setTpError('El codi i el nom són obligatoris'); return; }
    try {
      await api.post('/admin/tipus-projecte', tpNewForm);
      setTpNewForm(emptyTP()); setTpAdding(false); fetchTipusProjecte();
    } catch (e: any) { setTpError(e?.response?.data?.message || 'Error en crear'); }
  };

  const tpStartEdit = (item: TipusProjecte) => { setTpEditId(item.id); setTpEditForm({ code: item.code, name: item.name }); };
  const tpSaveEdit = async () => {
    if (!tpEditId) return;
    try { await api.put(`/admin/tipus-projecte/${tpEditId}`, { name: tpEditForm.name }); setTpEditId(null); fetchTipusProjecte(); }
    catch (e: any) { setTpError(e?.response?.data?.message || 'Error en desar'); }
  };
  const tpDragStart = (id: number) => setTpDragId(id);
  const tpDragOver = (e: React.DragEvent, id: number) => { e.preventDefault(); setTpDragOverId(id); };
  const tpDragEnd = () => { setTpDragId(null); setTpDragOverId(null); };
  const tpDrop = async (targetId: number) => {
    if (!tpDragId || tpDragId === targetId) { tpDragEnd(); return; }
    const reordered = [...tipusProjecte];
    const fromIdx = reordered.findIndex(t => t.id === tpDragId);
    const toIdx = reordered.findIndex(t => t.id === targetId);
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    setTipusProjecte(reordered);
    tpDragEnd();
    try { await api.put('/admin/tipus-projecte/reorder', { ids: reordered.map(t => t.id) }); }
    catch { setTpError("Error en desar l'ordre"); fetchTipusProjecte(); }
  };

  const tpToggleActive = async (item: TipusProjecte) => {
    try { await api.put(`/admin/tipus-projecte/${item.id}`, { isActive: item.isActive ? 0 : 1 }); fetchTipusProjecte(); }
    catch { setTpError('Error en canviar estat'); }
  };
  const tpDelete = async (item: TipusProjecte) => {
    if (!confirm(`Eliminar el tipus "${item.name}"?`)) return;
    try {
      const r = await api.delete<{ message?: string }>(`/admin/tipus-projecte/${item.id}`);
      showToast(r.message || 'Eliminat correctament', 'success');
      fetchTipusProjecte();
    } catch (e: any) { const msg = e?.response?.data?.message || 'Error en eliminar'; setTpError(msg); showToast(msg, 'error'); }
  };

  // ── Tipus OT CRUD ──────────────────────────────────────────────────────────────

  const wotSaveNew = async () => {
    if (!wotNewForm.code.trim() || !wotNewForm.name.trim()) { setWotError('El codi i el nom són obligatoris'); return; }
    try {
      await api.post('/admin/work-order-types', wotNewForm);
      setWotNewForm(emptyWOT());
      setWotAdding(false);
      fetchWorkOrderTypes();
    } catch (e: any) { setWotError(e?.response?.data?.message || 'Error en crear'); }
  };

  const wotStartEdit = (item: WorkOrderTypeAdmin) => {
    setWotEditId(item.id);
    setWotEditForm({ code: item.code, name: item.name, description: item.description || '' });
  };

  const wotSaveEdit = async () => {
    if (!wotEditId) return;
    try {
      await api.put(`/admin/work-order-types/${wotEditId}`, { name: wotEditForm.name, description: wotEditForm.description });
      setWotEditId(null);
      fetchWorkOrderTypes();
    } catch (e: any) { setWotError(e?.response?.data?.message || 'Error en desar'); }
  };

  const wotDragStart = (id: number) => setWotDragId(id);
  const wotDragOver = (e: React.DragEvent, id: number) => { e.preventDefault(); setWotDragOverId(id); };
  const wotDragEnd = () => { setWotDragId(null); setWotDragOverId(null); };
  const wotDrop = async (targetId: number) => {
    if (!wotDragId || wotDragId === targetId) { wotDragEnd(); return; }
    const reordered = [...workOrderTypes];
    const fromIdx = reordered.findIndex(t => t.id === wotDragId);
    const toIdx = reordered.findIndex(t => t.id === targetId);
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    setWorkOrderTypes(reordered);
    wotDragEnd();
    try { await api.put('/admin/work-order-types/reorder', { ids: reordered.map(t => t.id) }); }
    catch { setWotError("Error en desar l'ordre"); fetchWorkOrderTypes(); }
  };

  const wotToggleActive = async (item: WorkOrderTypeAdmin) => {
    try { await api.put(`/admin/work-order-types/${item.id}`, { isActive: item.isActive ? 0 : 1 }); fetchWorkOrderTypes(); }
    catch { setWotError('Error en canviar estat'); }
  };

  const wotDelete = async (item: WorkOrderTypeAdmin) => {
    if (!confirm(`Eliminar el tipus d'OT "${item.name}"?`)) return;
    try {
      const r = await api.delete<{ message?: string }>(`/admin/work-order-types/${item.id}`);
      showToast(r.message || 'Eliminat correctament', 'success');
      fetchWorkOrderTypes();
    } catch (e: any) { const msg = e?.response?.data?.message || 'Error en eliminar'; setWotError(msg); showToast(msg, 'error'); }
  };

  // ── Grup Equipament CRUD ──────────────────────────────────────────────────────

  const geSaveNew = async () => {
    if (!geNewForm.code.trim() || !geNewForm.name.trim()) { setGeError('El codi i el nom són obligatoris'); return; }
    try {
      await api.post('/catalog/grups-equipament', geNewForm);
      setGeNewForm(emptyGE()); setGeAdding(false); fetchGrupsEquipament();
    } catch (e: any) { setGeError(e?.response?.data?.message || 'Error en crear'); }
  };
  const geStartEdit = (item: GrupEquipament) => { setGeEditId(item.id); setGeEditForm({ code: item.code, name: item.name }); };
  const geSaveEdit = async () => {
    if (!geEditId) return;
    try { await api.put(`/catalog/grups-equipament/${geEditId}`, { name: geEditForm.name }); setGeEditId(null); fetchGrupsEquipament(); }
    catch (e: any) { setGeError(e?.response?.data?.message || 'Error en desar'); }
  };
  const geToggleActive = async (item: GrupEquipament) => {
    try { await api.put(`/catalog/grups-equipament/${item.id}`, { isActive: item.isActive ? 0 : 1 }); fetchGrupsEquipament(); }
    catch { setGeError('Error en canviar estat'); }
  };
  const geDelete = async (item: GrupEquipament) => {
    if (!confirm(`Eliminar el grup "${item.name}"?`)) return;
    try {
      const r = await api.delete<{ message?: string }>(`/catalog/grups-equipament/${item.id}`);
      showToast(r.message || 'Eliminat correctament', 'success');
      fetchGrupsEquipament();
    } catch (e: any) { const msg = e?.response?.data?.message || 'Error en eliminar'; setGeError(msg); showToast(msg, 'error'); }
  };

  // ── Subgrup Equipament CRUD ───────────────────────────────────────────────────

  const sgeSaveNew = async () => {
    if (!sgeNewForm.code.trim() || !sgeNewForm.name.trim()) { setSgeError('El codi i el nom són obligatoris'); return; }
    try {
      await api.post('/catalog/subgrups-equipament', sgeNewForm);
      setSgeNewForm(emptySGE()); setSgeAdding(false); fetchSubgrupsEquipament();
    } catch (e: any) { setSgeError(e?.response?.data?.message || 'Error en crear'); }
  };
  const sgeStartEdit = (item: SubgrupEquipament) => { setSgeEditId(item.id); setSgeEditForm({ code: item.code, name: item.name }); };
  const sgeSaveEdit = async () => {
    if (!sgeEditId) return;
    try { await api.put(`/catalog/subgrups-equipament/${sgeEditId}`, { name: sgeEditForm.name }); setSgeEditId(null); fetchSubgrupsEquipament(); }
    catch (e: any) { setSgeError(e?.response?.data?.message || 'Error en desar'); }
  };
  const sgeToggleActive = async (item: SubgrupEquipament) => {
    try { await api.put(`/catalog/subgrups-equipament/${item.id}`, { isActive: item.isActive ? 0 : 1 }); fetchSubgrupsEquipament(); }
    catch { setSgeError('Error en canviar estat'); }
  };
  const sgeDelete = async (item: SubgrupEquipament) => {
    if (!confirm(`Eliminar el subgrup "${item.name}"?`)) return;
    try {
      const r = await api.delete<{ message?: string }>(`/catalog/subgrups-equipament/${item.id}`);
      showToast(r.message || 'Eliminat correctament', 'success');
      fetchSubgrupsEquipament();
    } catch (e: any) { const msg = e?.response?.data?.message || 'Error en eliminar'; setSgeError(msg); showToast(msg, 'error'); }
  };

  // ── Àmbit Equipament CRUD ─────────────────────────────────────────────────────

  const aeSaveNew = async () => {
    if (!aeNewForm.code.trim() || !aeNewForm.name.trim()) { setAeError('El codi i el nom són obligatoris'); return; }
    try {
      await api.post('/catalog/ambits-equipament', aeNewForm);
      setAeNewForm(emptyAE()); setAeAdding(false); fetchAmbitsEquipament();
    } catch (e: any) { setAeError(e?.response?.data?.message || 'Error en crear'); }
  };
  const aeStartEdit = (item: AmbitEquipament) => { setAeEditId(item.id); setAeEditForm({ code: item.code, name: item.name }); };
  const aeSaveEdit = async () => {
    if (!aeEditId) return;
    try { await api.put(`/catalog/ambits-equipament/${aeEditId}`, { name: aeEditForm.name }); setAeEditId(null); fetchAmbitsEquipament(); }
    catch (e: any) { setAeError(e?.response?.data?.message || 'Error en desar'); }
  };
  const aeToggleActive = async (item: AmbitEquipament) => {
    try { await api.put(`/catalog/ambits-equipament/${item.id}`, { isActive: item.isActive ? 0 : 1 }); fetchAmbitsEquipament(); }
    catch { setAeError('Error en canviar estat'); }
  };
  const aeDelete = async (item: AmbitEquipament) => {
    if (!confirm(`Eliminar l'àmbit "${item.name}"?`)) return;
    try {
      const r = await api.delete<{ message?: string }>(`/catalog/ambits-equipament/${item.id}`);
      showToast(r.message || 'Eliminat correctament', 'success');
      fetchAmbitsEquipament();
    } catch (e: any) { const msg = e?.response?.data?.message || 'Error en eliminar'; setAeError(msg); showToast(msg, 'error'); }
  };

  // ── Grups Expedient Comercial CRUD ───────────────────────────────────────────

  const gecSaveNew = async () => {
    if (!gecNewForm.code.trim() || !gecNewForm.name.trim()) { setGecError('El codi i el nom són obligatoris'); return; }
    try {
      await api.post('/admin/grups-expedient-comercial', gecNewForm);
      setGecNewForm(emptyGEC()); setGecAdding(false); fetchGrupsEC();
    } catch (e: any) { setGecError(e?.response?.data?.message || 'Error en crear'); }
  };

  const gecStartEdit = (item: GrupExpedientComercial) => { setGecEditId(item.id); setGecEditForm({ code: item.code, name: item.name }); };
  const gecSaveEdit = async () => {
    if (!gecEditId) return;
    try { await api.put(`/admin/grups-expedient-comercial/${gecEditId}`, { name: gecEditForm.name }); setGecEditId(null); fetchGrupsEC(); }
    catch (e: any) { setGecError(e?.response?.data?.message || 'Error en desar'); }
  };

  const gecToggleActive = async (item: GrupExpedientComercial) => {
    try { await api.put(`/admin/grups-expedient-comercial/${item.id}`, { isActive: item.isActive ? 0 : 1 }); fetchGrupsEC(); }
    catch { setGecError('Error en canviar estat'); }
  };
  const gecDelete = async (item: GrupExpedientComercial) => {
    if (!confirm(`Eliminar el grup "${item.name}"?`)) return;
    try {
      const r = await api.delete<{ message?: string }>(`/admin/grups-expedient-comercial/${item.id}`);
      showToast(r.message || 'Eliminat correctament', 'success');
      fetchGrupsEC();
    } catch (e: any) { const msg = e?.response?.data?.message || 'Error en eliminar'; setGecError(msg); showToast(msg, 'error'); }
  };

  // ── Hores de Personal CRUD ─────────────────────────────────────────────────────

  const hpSaveNew = async () => {
    if (!hpNewForm.code.trim() || !hpNewForm.name.trim()) { setHpError('El codi i el nom són obligatoris'); return; }
    try {
      await api.post('/admin/hores-personal', { ...hpNewForm, hourlyRate: parseFloat(hpNewForm.hourlyRate.replace(',', '.')) || 0 });
      setHpNewForm(emptyHP()); setHpAdding(false); fetchHoresPersonal();
    } catch (e: any) { setHpError(e?.response?.data?.message || 'Error en crear'); }
  };

  const hpStartEdit = (item: HoraPersonal) => { setHpEditId(item.id); setHpEditForm({ code: item.code, name: item.name, hourlyRate: String(item.hourlyRate).replace('.', ',') }); };
  const hpSaveEdit = async () => {
    if (!hpEditId) return;
    try {
      await api.put(`/admin/hores-personal/${hpEditId}`, { name: hpEditForm.name, hourlyRate: parseFloat(hpEditForm.hourlyRate.replace(',', '.')) || 0 });
      setHpEditId(null); fetchHoresPersonal();
    } catch (e: any) { setHpError(e?.response?.data?.message || 'Error en desar'); }
  };

  const hpToggleActive = async (item: HoraPersonal) => {
    try { await api.put(`/admin/hores-personal/${item.id}`, { isActive: item.isActive ? 0 : 1 }); fetchHoresPersonal(); }
    catch { setHpError('Error en canviar estat'); }
  };
  const hpDelete = async (item: HoraPersonal) => {
    if (!confirm(`Eliminar "${item.name}"?`)) return;
    try {
      const r = await api.delete<{ message?: string }>(`/admin/hores-personal/${item.id}`);
      showToast(r.message || 'Eliminat correctament', 'success');
      fetchHoresPersonal();
    } catch (e: any) { const msg = e?.response?.data?.message || 'Error en eliminar'; setHpError(msg); showToast(msg, 'error'); }
  };

  // ── Composite Codes CRUD ──────────────────────────────────────────────────────

  const ccSaveNew = async () => {
    if (!ccNewForm.code || !ccNewForm.name) { setCcError('Codi i nom són obligatoris'); return; }
    try {
      await api.post('/catalog/composite-codes', {
        ...ccNewForm,
        marginPercentage: ccNewForm.marginPercentage ? parseFloat(ccNewForm.marginPercentage.replace(',', '.')) || 0 : undefined,
      });
      setCcNewForm(emptyCC());
      setCcAdding(false);
      fetchCompositeCodes();
    } catch (e: any) {
      setCcError(e?.response?.data?.message || 'Error en crear');
    }
  };

  const ccStartEdit = (item: CompositeCode) => {
    setCcEditId(item.id);
    setCcEditForm({
      code: item.code, name: item.name,
      description: item.description || '',
      marginPercentage: item.marginPercentage != null ? String(item.marginPercentage).replace('.', ',') : '',
      brandId: item.brandId || '',
    });
  };

  const ccSaveEdit = async () => {
    if (!ccEditId) return;
    try {
      await api.put(`/catalog/composite-codes/${ccEditId}`, {
        ...ccEditForm,
        marginPercentage: ccEditForm.marginPercentage ? parseFloat(ccEditForm.marginPercentage.replace(',', '.')) || 0 : null,
      });
      setCcEditId(null);
      fetchCompositeCodes();
    } catch (e: any) {
      setCcError(e?.response?.data?.message || 'Error en desar');
    }
  };

  const ccDragStart = (id: number) => setCcDragId(id);
  const ccDragOver = (e: React.DragEvent, id: number) => { e.preventDefault(); setCcDragOverId(id); };
  const ccDragEnd = () => { setCcDragId(null); setCcDragOverId(null); };
  const ccDrop = async (targetId: number) => {
    if (!ccDragId || ccDragId === targetId) { ccDragEnd(); return; }
    const reordered = [...compositeCodes];
    const from = reordered.findIndex(c => c.id === ccDragId);
    const to = reordered.findIndex(c => c.id === targetId);
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    setCompositeCodes(reordered);
    ccDragEnd();
    try { await api.put('/catalog/composite-codes/reorder', { ids: reordered.map(c => c.id) }); }
    catch (err: any) {
      const msg = err?.response?.data?.message || "Error en desar l'ordre. Comprova que la base de dades està actualitzada.";
      console.error('[ccDrop] reorder failed:', err);
      await fetchCompositeCodes();
      setCcError(msg);
    }
  };

  const ccToggleActive = async (item: CompositeCode) => {
    try {
      await api.put(`/catalog/composite-codes/${item.id}`, { isActive: item.isActive ? 0 : 1 });
      fetchCompositeCodes();
    } catch { setCcError('Error en canviar estat'); }
  };

  const ccDelete = async (item: CompositeCode) => {
    if (!confirm(`Eliminar el tipus de producte "${item.name}"?`)) return;
    try {
      const r = await api.delete<{ message?: string }>(`/catalog/composite-codes/${item.id}`);
      showToast(r.message || 'Eliminat correctament', 'success');
      fetchCompositeCodes();
    } catch (e: any) { const msg = e?.response?.data?.message || 'Error en eliminar'; setCcError(msg); showToast(msg, 'error'); }
  };

  // ── Client Types CRUD ─────────────────────────────────────────────────────────

  const ctSaveNew = async () => {
    if (!ctNewForm.code || !ctNewForm.name) { setCtError('Codi i nom són obligatoris'); return; }
    try {
      await api.post('/catalog/client-types', { ...ctNewForm, naturalesaClientId: ctNewForm.naturalesaClientId || null });
      setCtNewForm(emptyCT());
      setCtAdding(false);
      fetchClientTypes();
    } catch (e: any) {
      setCtError(e?.response?.data?.message || 'Error en crear');
    }
  };

  const ctStartEdit = (item: ClientType) => {
    setCtEditId(item.id);
    setCtEditForm({
      code: item.code,
      name: item.name,
      description: item.description || '',
      naturalesaClientId: item.naturalesaClientId ? String(item.naturalesaClientId) : '',
    });
  };

  const ctSaveEdit = async () => {
    if (!ctEditId) return;
    try {
      await api.put(`/catalog/client-types/${ctEditId}`, { ...ctEditForm, naturalesaClientId: ctEditForm.naturalesaClientId || null });
      setCtEditId(null);
      fetchClientTypes();
    } catch (e: any) {
      setCtError(e?.response?.data?.message || 'Error en desar');
    }
  };

  const ctDragStart = (id: number) => setCtDragId(id);
  const ctDragOver = (e: React.DragEvent, id: number) => { e.preventDefault(); setCtDragOverId(id); };
  const ctDragEnd = () => { setCtDragId(null); setCtDragOverId(null); };
  const ctDrop = async (targetId: number) => {
    if (!ctDragId || ctDragId === targetId) { ctDragEnd(); return; }
    const reordered = [...clientTypes];
    const from = reordered.findIndex(c => c.id === ctDragId);
    const to = reordered.findIndex(c => c.id === targetId);
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    setClientTypes(reordered);
    ctDragEnd();
    try { await api.put('/catalog/client-types/reorder', { ids: reordered.map(c => c.id) }); }
    catch { setCtError("Error en desar l'ordre"); fetchClientTypes(); }
  };

  const ctToggleActive = async (item: ClientType) => {
    try {
      await api.put(`/catalog/client-types/${item.id}`, { isActive: item.isActive ? 0 : 1 });
      fetchClientTypes();
    } catch { setCtError('Error en canviar estat'); }
  };

  const ctDelete = async (item: ClientType) => {
    if (!confirm(`Eliminar l'activitat de client "${item.name}"?`)) return;
    try {
      const r = await api.delete<{ message?: string }>(`/catalog/client-types/${item.id}`);
      showToast(r.message || 'Eliminat correctament', 'success');
      fetchClientTypes();
    } catch (e: any) { const msg = e?.response?.data?.message || 'Error en eliminar'; setCtError(msg); showToast(msg, 'error'); }
  };

  // ── Naturalesa Client CRUD ─────────────────────────────────────────────────────

  const ncSaveNew = async () => {
    if (!ncNewForm.code || !ncNewForm.name) { setNcError('Codi i nom són obligatoris'); return; }
    try {
      await api.post('/catalog/naturalesa-client', ncNewForm);
      setNcNewForm(emptyNC());
      setNcAdding(false);
      fetchNaturalesaClient();
    } catch (e: any) { setNcError(e?.response?.data?.message || 'Error en crear'); }
  };

  const ncStartEdit = (item: NaturalesaClient) => {
    setNcEditId(item.id);
    setNcEditForm({ code: item.code, name: item.name, description: item.description || '' });
  };

  const ncSaveEdit = async () => {
    if (!ncEditId) return;
    try {
      await api.put(`/catalog/naturalesa-client/${ncEditId}`, ncEditForm);
      setNcEditId(null);
      fetchNaturalesaClient();
    } catch (e: any) { setNcError(e?.response?.data?.message || 'Error en desar'); }
  };

  const ncDragStart = (id: number) => setNcDragId(id);
  const ncDragOver = (e: React.DragEvent, id: number) => { e.preventDefault(); setNcDragOverId(id); };
  const ncDragEnd = () => { setNcDragId(null); setNcDragOverId(null); };
  const ncDrop = async (targetId: number) => {
    if (!ncDragId || ncDragId === targetId) { ncDragEnd(); return; }
    const reordered = [...naturalesaClient];
    const from = reordered.findIndex(c => c.id === ncDragId);
    const to = reordered.findIndex(c => c.id === targetId);
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    setNaturalesaClient(reordered);
    ncDragEnd();
    try { await api.put('/catalog/naturalesa-client/reorder', { ids: reordered.map(c => c.id) }); }
    catch { setNcError("Error en desar l'ordre"); fetchNaturalesaClient(); }
  };

  const ncToggleActive = async (item: NaturalesaClient) => {
    try {
      await api.put(`/catalog/naturalesa-client/${item.id}`, { isActive: item.isActive ? 0 : 1 });
      fetchNaturalesaClient();
    } catch { setNcError('Error en canviar estat'); }
  };

  const ncDelete = async (item: NaturalesaClient) => {
    if (!confirm(`Eliminar la naturalesa "${item.name}"?`)) return;
    try {
      const r = await api.delete<{ message?: string }>(`/catalog/naturalesa-client/${item.id}`);
      showToast(r.message || 'Eliminat correctament', 'success');
      fetchNaturalesaClient();
    } catch (e: any) { const msg = e?.response?.data?.message || 'Error en eliminar'; setNcError(msg); showToast(msg, 'error'); }
  };

  // ── Rol Client CRUD ────────────────────────────────────────────────────────────

  const rcSaveNew = async () => {
    if (!rcNewForm.code || !rcNewForm.name) { setRcError('Codi i nom són obligatoris'); return; }
    try {
      await api.post('/catalog/rol-client', rcNewForm);
      setRcNewForm(emptyRC());
      setRcAdding(false);
      fetchRolClient();
    } catch (e: any) { setRcError(e?.response?.data?.message || 'Error en crear'); }
  };

  const rcStartEdit = (item: RolClient) => {
    setRcEditId(item.id);
    setRcEditForm({ code: item.code, name: item.name, description: item.description || '' });
  };

  const rcSaveEdit = async () => {
    if (!rcEditId) return;
    try {
      await api.put(`/catalog/rol-client/${rcEditId}`, rcEditForm);
      setRcEditId(null);
      fetchRolClient();
    } catch (e: any) { setRcError(e?.response?.data?.message || 'Error en desar'); }
  };

  const rcDragStart = (id: number) => setRcDragId(id);
  const rcDragOver = (e: React.DragEvent, id: number) => { e.preventDefault(); setRcDragOverId(id); };
  const rcDragEnd = () => { setRcDragId(null); setRcDragOverId(null); };
  const rcDrop = async (targetId: number) => {
    if (!rcDragId || rcDragId === targetId) { rcDragEnd(); return; }
    const reordered = [...rolClient];
    const from = reordered.findIndex(c => c.id === rcDragId);
    const to = reordered.findIndex(c => c.id === targetId);
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    setRolClient(reordered);
    rcDragEnd();
    try { await api.put('/catalog/rol-client/reorder', { ids: reordered.map(c => c.id) }); }
    catch { setRcError("Error en desar l'ordre"); fetchRolClient(); }
  };

  const rcToggleActive = async (item: RolClient) => {
    try {
      await api.put(`/catalog/rol-client/${item.id}`, { isActive: item.isActive ? 0 : 1 });
      fetchRolClient();
    } catch { setRcError('Error en canviar estat'); }
  };

  const rcDelete = async (item: RolClient) => {
    if (!confirm(`Eliminar el rol "${item.name}"?`)) return;
    try {
      const r = await api.delete<{ message?: string }>(`/catalog/rol-client/${item.id}`);
      showToast(r.message || 'Eliminat correctament', 'success');
      fetchRolClient();
    } catch (e: any) { const msg = e?.response?.data?.message || 'Error en eliminar'; setRcError(msg); showToast(msg, 'error'); }
  };

  // ── Estat Comercial CRUD ───────────────────────────────────────────────────────

  const esSaveNew = async () => {
    if (!esNewForm.code || !esNewForm.name) { setEsError('Codi i nom són obligatoris'); return; }
    try {
      await api.post('/catalog/estats-comercials', esNewForm);
      setEsNewForm(emptyES());
      setEsAdding(false);
      fetchEstatsComercials();
    } catch (e: any) { setEsError(e?.response?.data?.message || 'Error en crear'); }
  };

  const esStartEdit = (item: EstatComercial) => {
    setEsEditId(item.id);
    setEsEditForm({ code: item.code, name: item.name, description: item.description || '' });
  };

  const esSaveEdit = async () => {
    if (!esEditId) return;
    try {
      await api.put(`/catalog/estats-comercials/${esEditId}`, esEditForm);
      setEsEditId(null);
      fetchEstatsComercials();
    } catch (e: any) { setEsError(e?.response?.data?.message || 'Error en desar'); }
  };

  const esDragStart = (id: number) => setEsDragId(id);
  const esDragOver = (e: React.DragEvent, id: number) => { e.preventDefault(); setEsDragOverId(id); };
  const esDragEnd = () => { setEsDragId(null); setEsDragOverId(null); };
  const esDrop = async (targetId: number) => {
    if (!esDragId || esDragId === targetId) { esDragEnd(); return; }
    const reordered = [...estatsComercials];
    const from = reordered.findIndex(c => c.id === esDragId);
    const to = reordered.findIndex(c => c.id === targetId);
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    setEstatsComercials(reordered);
    esDragEnd();
    try { await api.put('/catalog/estats-comercials/reorder', { ids: reordered.map(c => c.id) }); }
    catch { setEsError("Error en desar l'ordre"); fetchEstatsComercials(); }
  };

  const esToggleActive = async (item: EstatComercial) => {
    try {
      await api.put(`/catalog/estats-comercials/${item.id}`, { isActive: item.isActive ? 0 : 1 });
      fetchEstatsComercials();
    } catch { setEsError('Error en canviar estat'); }
  };

  const esDelete = async (item: EstatComercial) => {
    if (!confirm(`Eliminar l'estat "${item.name}"?`)) return;
    try {
      const r = await api.delete<{ message?: string }>(`/catalog/estats-comercials/${item.id}`);
      showToast(r.message || 'Eliminat correctament', 'success');
      fetchEstatsComercials();
    } catch (e: any) { const msg = e?.response?.data?.message || 'Error en eliminar'; setEsError(msg); showToast(msg, 'error'); }
  };

  // ── Element Types CRUD ────────────────────────────────────────────────────────

  const etSaveNew = async () => {
    if (!etNewForm.code || !etNewForm.name) { setEtError('Codi i nom són obligatoris'); return; }
    try {
      await api.post('/catalog/element-types', etNewForm);
      setEtNewForm(emptyET());
      setEtAdding(false);
      fetchElementTypes();
    } catch (e: any) {
      setEtError(e?.response?.data?.message || 'Error en crear');
    }
  };

  const etStartEdit = (item: ElementType) => {
    setEtEditId(item.id);
    setEtEditForm({ code: item.code, name: item.name, description: item.description || '' });
  };

  const etSaveEdit = async () => {
    if (!etEditId) return;
    try {
      await api.put(`/catalog/element-types/${etEditId}`, etEditForm);
      setEtEditId(null);
      fetchElementTypes();
    } catch (e: any) {
      setEtError(e?.response?.data?.message || 'Error en desar');
    }
  };

  const etToggleActive = async (item: ElementType) => {
    try {
      await api.put(`/catalog/element-types/${item.id}`, { isActive: item.isActive ? 0 : 1 });
      fetchElementTypes();
    } catch { setEtError('Error en canviar estat'); }
  };

  const etDelete = async (item: ElementType) => {
    if (!confirm(`Eliminar el tipus d'element "${item.name}"?`)) return;
    try {
      const r = await api.delete<{ message?: string }>(`/catalog/element-types/${item.id}`);
      showToast(r.message || 'Eliminat correctament', 'success');
      fetchElementTypes();
    } catch (e: any) { const msg = e?.response?.data?.message || 'Error en eliminar'; setEtError(msg); showToast(msg, 'error'); }
  };

  // ── Material Types CRUD ───────────────────────────────────────────────────────

  const mtSaveNew = async () => {
    if (!mtNewForm.code || !mtNewForm.name) { setMtError('Codi i nom són obligatoris'); return; }
    try {
      await api.post('/catalog/material-types', { ...mtNewForm, parentId: mtNewForm.parentId || null, elementTypeId: mtNewForm.elementTypeId || null });
      setMtNewForm(emptyMT());
      setMtAdding(false);
      fetchMaterialTypes();
    } catch (e: any) {
      setMtError(e?.response?.data?.message || 'Error en crear');
    }
  };

  const mtStartEdit = (item: MaterialType) => {
    setMtEditId(item.id);
    setMtEditForm({
      code: item.code,
      name: item.name,
      description: item.description || '',
      parentId: item.parentId ? String(item.parentId) : '',
      elementTypeId: item.elementTypeId ? String(item.elementTypeId) : '',
    });
  };

  const mtSaveEdit = async () => {
    if (!mtEditId) return;
    try {
      await api.put(`/catalog/material-types/${mtEditId}`, {
        ...mtEditForm,
        parentId: mtEditForm.parentId || null,
        elementTypeId: mtEditForm.elementTypeId || null,
      });
      setMtEditId(null);
      fetchMaterialTypes();
    } catch (e: any) {
      setMtError(e?.response?.data?.message || 'Error en desar');
    }
  };

  const mtDragStart = (id: number) => setMtDragId(id);
  const mtDragOver = (e: React.DragEvent, id: number) => { e.preventDefault(); setMtDragOverId(id); };
  const mtDragEnd = () => { setMtDragId(null); setMtDragOverId(null); };
  const mtDrop = async (targetId: number) => {
    if (!mtDragId || mtDragId === targetId) { mtDragEnd(); return; }
    const reordered = [...materialTypes];
    const fromIdx = reordered.findIndex(m => m.id === mtDragId);
    const toIdx = reordered.findIndex(m => m.id === targetId);
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    setMaterialTypes(reordered);
    mtDragEnd();
    try {
      await api.put('/catalog/material-types/reorder', { ids: reordered.map(m => m.id) });
    } catch { setMtError('Error en desar l\'ordre'); fetchMaterialTypes(); }
  };

  const mtToggleActive = async (item: MaterialType) => {
    try {
      await api.put(`/catalog/material-types/${item.id}`, { isActive: item.isActive ? 0 : 1 });
      fetchMaterialTypes();
    } catch { setMtError('Error en canviar estat'); }
  };

  const mtDelete = async (item: MaterialType) => {
    if (!confirm(`Eliminar la família "${item.name}"?`)) return;
    try {
      const r = await api.delete<{ message?: string }>(`/catalog/material-types/${item.id}`);
      showToast(r.message || 'Eliminat correctament', 'success');
      fetchMaterialTypes();
    } catch (e: any) { const msg = e?.response?.data?.message || 'Error en eliminar'; setMtError(msg); showToast(msg, 'error'); }
  };

  // ── Node Types CRUD ───────────────────────────────────────────────────────────

  const ntSaveNew = async () => {
    if (!ntNewForm.code || !ntNewForm.name) { setNtError('Codi i nom són obligatoris'); return; }
    try {
      await api.post('/catalog/node-types', ntNewForm);
      setNtNewForm(emptyNT());
      setNtAdding(false);
      fetchNodeTypes();
    } catch (e: any) {
      setNtError(e?.response?.data?.message || 'Error en crear');
    }
  };

  const ntStartEdit = (item: NodeType) => {
    setNtEditCode(item.code);
    setNtEditForm({ code: item.code, name: item.name, description: item.description || '', badgeColor: item.badgeColor });
  };

  const ntSaveEdit = async () => {
    if (!ntEditCode) return;
    try {
      await api.put(`/catalog/node-types/${ntEditCode}`, ntEditForm);
      setNtEditCode(null);
      fetchNodeTypes();
    } catch (e: any) {
      setNtError(e?.response?.data?.message || 'Error en desar');
    }
  };

  const ntToggleActive = async (item: NodeType) => {
    try {
      await api.put(`/catalog/node-types/${item.code}`, { isActive: item.isActive ? 0 : 1 });
      fetchNodeTypes();
    } catch { setNtError('Error en canviar estat'); }
  };

  const ntDelete = async (item: NodeType) => {
    if (!confirm(`Eliminar el tipus d'escandall "${item.name}"?`)) return;
    try {
      const r = await api.delete<{ message?: string }>(`/catalog/node-types/${item.code}`);
      showToast(r.message || 'Eliminat correctament', 'success');
      fetchNodeTypes();
    } catch (e: any) { const msg = e?.response?.data?.message || 'Error en eliminar'; setNtError(msg); showToast(msg, 'error'); }
  };

  // ── Helpers ───────────────────────────────────────────────────────────────────

  const rootMaterialTypes = materialTypes.filter(mt => !mt.parentId && mt.isActive);

  const visibleCC = showInactive ? compositeCodes : compositeCodes.filter(cc => cc.isActive);
  const visibleCT = showInactive ? clientTypes : clientTypes.filter(ct => ct.isActive);
  const visibleET = showInactive ? elementTypes : elementTypes.filter(et => et.isActive);
  const visibleMT = showInactive ? materialTypes : materialTypes.filter(mt => mt.isActive);
  const visibleNT = showInactive ? nodeTypes : nodeTypes.filter(nt => nt.isActive);
  const visibleTP = showInactive ? tipusProjecte : tipusProjecte.filter(tp => tp.isActive);
  const visibleWOT = showInactive ? workOrderTypes : workOrderTypes.filter(t => t.isActive);
  const visibleGE = showInactive ? grupsEquipament : grupsEquipament.filter(ge => ge.isActive);
  const visibleSGE = showInactive ? subgrupsEquipament : subgrupsEquipament.filter(sge => sge.isActive);
  const visibleAE = showInactive ? ambitsEquipament : ambitsEquipament.filter(ae => ae.isActive);
  const visibleGEC = showInactive ? grupsEC : grupsEC.filter(g => g.isActive);
  const visibleHP = showInactive ? hoursPersonal : hoursPersonal.filter(hp => hp.isActive);
  const visibleNC = showInactive ? naturalesaClient : naturalesaClient.filter(nc => nc.isActive);
  const visibleRC = showInactive ? rolClient : rolClient.filter(rc => rc.isActive);
  const visibleES = showInactive ? estatsComercials : estatsComercials.filter(es => es.isActive);
  const modulsAmbTaules = MODULS.filter((m) => TAB_DEFS.some((t) => t.modul === m));

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Tipus de Catàleg</h1>
          <p className="text-muted-foreground text-sm mt-1">Gestiona els tipus de famílies d'elements i materials del sistema</p>
        </div>
        <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="rounded"
          />
          Mostrar inactius
        </label>
      </div>

      {/* Targetes de mòdul */}
      <div className={GRID_COLS_CLASS[modulsAmbTaules.length] ?? 'grid gap-3 grid-cols-4'}>
        {modulsAmbTaules.map((modul) => {
          const Icon = MODUL_ICON[modul];
          const c = MODUL_CARD_COLOR[modul];
          const active = activeModul === modul;
          return (
            <button
              key={modul}
              onClick={() => {
                setActiveModul(modul);
                const firstTab = TAB_DEFS.find((t) => t.modul === modul);
                if (firstTab) setActiveTab(firstTab.key as typeof activeTab);
              }}
              className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border transition-all ${
                active ? `${c.border} ${c.bg}` : 'border-border bg-white hover:shadow-sm'
              }`}
            >
              <div className={`p-1.5 rounded-lg shrink-0 ${c.iconBg}`}>
                <Icon className={`h-4 w-4 ${c.icon}`} />
              </div>
              <span className="text-xs font-semibold">{modul}</span>
            </button>
          );
        })}
      </div>

      {/* Pestanyes de la taula dins del mòdul actiu */}
      <div className="flex flex-wrap gap-1 border-b border-border pb-3">
        {TAB_DEFS.filter((t) => t.modul === activeModul).map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key as typeof activeTab)}
            className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
              activeTab === key
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── Composite Codes Tab ───────────────────────────────────────────────── */}
      {activeTab === 'productes' && (
        <div className="space-y-4">
          {ccError && (
            <div className="rounded border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-700 flex justify-between">
              {ccError}
              <button onClick={() => setCcError(null)}><X className="h-4 w-4" /></button>
            </div>
          )}

          <div className="rounded-xl border bg-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="px-2 py-2 w-6"></th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground w-20">Codi</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground w-56">Nom</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground w-40">Descripció</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold uppercase text-muted-foreground w-28">Marge comercial %</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground w-40">Marca</th>
                  <th className="px-4 py-2 text-center text-xs font-semibold uppercase text-muted-foreground w-20">Estat</th>
                  <th className="px-4 py-2 w-20"></th>
                </tr>
              </thead>
              <tbody>
                {ccLoading ? (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">Carregant...</td></tr>
                ) : visibleCC.length === 0 && !ccAdding ? (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">Sense resultats</td></tr>
                ) : (
                  visibleCC.map((item) => (
                    <tr
                      key={item.id}
                      draggable={ccEditId !== item.id}
                      onDragStart={() => ccDragStart(item.id)}
                      onDragOver={(e) => ccDragOver(e, item.id)}
                      onDrop={() => ccDrop(item.id)}
                      onDragEnd={ccDragEnd}
                      className={`border-b last:border-0 transition-colors ${!item.isActive ? 'opacity-50' : ''} ${ccDragOverId === item.id && ccDragId !== item.id ? 'bg-blue-50 border-t-2 border-t-blue-400' : ''}`}
                    >
                      {ccEditId === item.id ? (
                        <>
                          <td className="px-2 py-2 text-slate-300 cursor-default">⠿</td>
                          <td className="px-4 py-2"><InlineInput value={ccEditForm.code} onChange={(v) => setCcEditForm(f => ({ ...f, code: v }))} placeholder="CODI" /></td>
                          <td className="px-4 py-2"><InlineInput value={ccEditForm.name} onChange={(v) => setCcEditForm(f => ({ ...f, name: v }))} placeholder="Nom" /></td>
                          <td className="px-4 py-2"><InlineInput value={ccEditForm.description} onChange={(v) => setCcEditForm(f => ({ ...f, description: v }))} placeholder="Descripció" /></td>
                          <td className="px-4 py-2">
                            <InlineInput value={ccEditForm.marginPercentage} onChange={(v) => setCcEditForm(f => ({ ...f, marginPercentage: v }))} placeholder="0,00" className="text-right" />
                          </td>
                          <td className="px-4 py-2">
                            <select
                              value={ccEditForm.brandId}
                              onChange={(e) => setCcEditForm(f => ({ ...f, brandId: e.target.value }))}
                              className="w-full px-2 py-1 border border-gray-200 rounded text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                            >
                              <option value="">— Sense marca —</option>
                              {brands.map((b) => (
                                <option key={b.id} value={b.id}>{b.name}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-4 py-2"></td>
                          <td className="px-4 py-2">
                            <div className="flex gap-1 justify-end">
                              <button onClick={ccSaveEdit} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                              <button onClick={() => setCcEditId(null)} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-2 py-2 text-slate-300 cursor-grab active:cursor-grabbing select-none">⠿</td>
                          <td className="px-4 py-2 font-mono font-medium text-xs">{item.code}</td>
                          <td className="px-4 py-2 text-xs">{item.name}</td>
                          <td className="px-4 py-2 text-muted-foreground text-xs">{item.description || '-'}</td>
                          <td className="px-4 py-2 text-muted-foreground text-xs text-right tabular-nums">
                            {item.marginPercentage != null ? `${Number(item.marginPercentage).toLocaleString('ca-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %` : '-'}
                          </td>
                          <td className="px-4 py-2 text-muted-foreground text-xs">{item.brandName || '-'}</td>
                          <td className="px-4 py-2 text-center">
                            <button onClick={() => ccToggleActive(item)} title={item.isActive ? 'Desactivar' : 'Activar'}>
                              {item.isActive
                                ? <ToggleRight className="h-5 w-5 text-green-600 mx-auto" />
                                : <ToggleLeft className="h-5 w-5 text-muted-foreground mx-auto" />}
                            </button>
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex gap-1 justify-end">
                              <button onClick={() => ccStartEdit(item)} className="p-1 hover:bg-muted rounded"><Edit2 className="h-4 w-4" /></button>
                              <button onClick={() => ccDelete(item)} className="p-1 hover:bg-red-50 rounded text-red-500"><X className="h-4 w-4" /></button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))
                )}

                {ccAdding && (
                  <tr className="border-b bg-blue-50/40">
                    <td className="px-2 py-2"></td>
                    <td className="px-4 py-2"><InlineInput value={ccNewForm.code} onChange={(v) => setCcNewForm(f => ({ ...f, code: v }))} placeholder="CODI" /></td>
                    <td className="px-4 py-2"><InlineInput value={ccNewForm.name} onChange={(v) => setCcNewForm(f => ({ ...f, name: v }))} placeholder="Nom" /></td>
                    <td className="px-4 py-2"><InlineInput value={ccNewForm.description} onChange={(v) => setCcNewForm(f => ({ ...f, description: v }))} placeholder="Descripció (opcional)" /></td>
                    <td className="px-4 py-2">
                      <InlineInput value={ccNewForm.marginPercentage} onChange={(v) => setCcNewForm(f => ({ ...f, marginPercentage: v }))} placeholder="0,00" className="text-right" />
                    </td>
                    <td className="px-4 py-2">
                      <select
                        value={ccNewForm.brandId}
                        onChange={(e) => setCcNewForm(f => ({ ...f, brandId: e.target.value }))}
                        className="w-full px-2 py-1 border border-gray-200 rounded text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                      >
                        <option value="">— Sense marca —</option>
                        {brands.map((b) => (
                          <option key={b.id} value={b.id}>{b.name}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-2"></td>
                    <td className="px-4 py-2">
                      <div className="flex gap-1 justify-end">
                        <button onClick={ccSaveNew} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                        <button onClick={() => { setCcAdding(false); setCcNewForm(emptyCC()); }} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {!ccAdding && (
            <Button size="sm" variant="outline" onClick={() => { setCcAdding(true); setCcEditId(null); }}>
              <Plus className="h-4 w-4 mr-1" />
              Nou tipus de producte
            </Button>
          )}
        </div>
      )}

      {/* ── Client Types Tab ──────────────────────────────────────────────────── */}
      {activeTab === 'clients' && (
        <div className="space-y-4">
          {ctError && (
            <div className="rounded border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-700 flex justify-between">
              {ctError}
              <button onClick={() => setCtError(null)}><X className="h-4 w-4" /></button>
            </div>
          )}

          <div className="rounded-xl border bg-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="px-2 py-2 w-6"></th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground w-36">Codi</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground">Nom</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground">Descripció</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground w-48">Naturalesa del client</th>
                  <th className="px-4 py-2 text-center text-xs font-semibold uppercase text-muted-foreground w-20">Estat</th>
                  <th className="px-4 py-2 w-20"></th>
                </tr>
              </thead>
              <tbody>
                {ctLoading ? (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">Carregant...</td></tr>
                ) : visibleCT.length === 0 && !ctAdding ? (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">Sense resultats</td></tr>
                ) : (
                  visibleCT.map((item) => (
                    <tr
                      key={item.id}
                      draggable={ctEditId !== item.id}
                      onDragStart={() => ctDragStart(item.id)}
                      onDragOver={(e) => ctDragOver(e, item.id)}
                      onDrop={() => ctDrop(item.id)}
                      onDragEnd={ctDragEnd}
                      className={`border-b last:border-0 transition-colors ${!item.isActive ? 'opacity-50' : ''} ${ctDragOverId === item.id && ctDragId !== item.id ? 'bg-blue-50 border-t-2 border-t-blue-400' : ''}`}
                    >
                      {ctEditId === item.id ? (
                        <>
                          <td className="px-2 py-2 text-slate-300 cursor-default">⠿</td>
                          <td className="px-4 py-2"><InlineInput value={ctEditForm.code} onChange={(v) => setCtEditForm(f => ({ ...f, code: v }))} placeholder="CODI" /></td>
                          <td className="px-4 py-2"><InlineInput value={ctEditForm.name} onChange={(v) => setCtEditForm(f => ({ ...f, name: v }))} placeholder="Nom" /></td>
                          <td className="px-4 py-2"><InlineInput value={ctEditForm.description} onChange={(v) => setCtEditForm(f => ({ ...f, description: v }))} placeholder="Descripció" /></td>
                          <td className="px-4 py-2">
                            <select
                              value={ctEditForm.naturalesaClientId}
                              onChange={(e) => setCtEditForm(f => ({ ...f, naturalesaClientId: e.target.value }))}
                              className="h-7 text-xs border border-input rounded px-2 bg-background w-full"
                            >
                              <option value="">— Selecciona —</option>
                              {naturalesaClient.filter(nc => nc.isActive).map(nc => (
                                <option key={nc.id} value={nc.id}>{nc.name}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-4 py-2"></td>
                          <td className="px-4 py-2">
                            <div className="flex gap-1 justify-end">
                              <button onClick={ctSaveEdit} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                              <button onClick={() => setCtEditId(null)} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-2 py-2 text-slate-300 cursor-grab active:cursor-grabbing select-none">⠿</td>
                          <td className="px-4 py-2 font-mono font-medium">{item.code}</td>
                          <td className="px-4 py-2 font-medium">{item.name}</td>
                          <td className="px-4 py-2 text-muted-foreground">{item.description || '-'}</td>
                          <td className="px-4 py-2 text-muted-foreground">{item.naturalesaClient?.name || '-'}</td>
                          <td className="px-4 py-2 text-center">
                            <button onClick={() => ctToggleActive(item)} title={item.isActive ? 'Desactivar' : 'Activar'}>
                              {item.isActive
                                ? <ToggleRight className="h-5 w-5 text-green-600 mx-auto" />
                                : <ToggleLeft className="h-5 w-5 text-muted-foreground mx-auto" />}
                            </button>
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex gap-1 justify-end">
                              <button onClick={() => ctStartEdit(item)} className="p-1 hover:bg-muted rounded"><Edit2 className="h-4 w-4" /></button>
                              <button onClick={() => ctDelete(item)} className="p-1 hover:bg-red-50 rounded text-red-500"><X className="h-4 w-4" /></button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))
                )}

                {/* New row */}
                {ctAdding && (
                  <tr className="border-b bg-blue-50/40">
                    <td className="px-2 py-2"></td>
                    <td className="px-4 py-2"><InlineInput value={ctNewForm.code} onChange={(v) => setCtNewForm(f => ({ ...f, code: v }))} placeholder="CODI" /></td>
                    <td className="px-4 py-2"><InlineInput value={ctNewForm.name} onChange={(v) => setCtNewForm(f => ({ ...f, name: v }))} placeholder="Nom" /></td>
                    <td className="px-4 py-2"><InlineInput value={ctNewForm.description} onChange={(v) => setCtNewForm(f => ({ ...f, description: v }))} placeholder="Descripció (opcional)" /></td>
                    <td className="px-4 py-2">
                      <select
                        value={ctNewForm.naturalesaClientId}
                        onChange={(e) => setCtNewForm(f => ({ ...f, naturalesaClientId: e.target.value }))}
                        className="h-7 text-xs border border-input rounded px-2 bg-background w-full"
                      >
                        <option value="">— Selecciona —</option>
                        {naturalesaClient.filter(nc => nc.isActive).map(nc => (
                          <option key={nc.id} value={nc.id}>{nc.name}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-2"></td>
                    <td className="px-4 py-2">
                      <div className="flex gap-1 justify-end">
                        <button onClick={ctSaveNew} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                        <button onClick={() => { setCtAdding(false); setCtNewForm(emptyCT()); }} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {!ctAdding && (
            <Button size="sm" variant="outline" onClick={() => { setCtAdding(true); setCtEditId(null); }}>
              <Plus className="h-4 w-4 mr-1" />
              Nova activitat client
            </Button>
          )}
        </div>
      )}

      {/* ── Arbre d'elements (Tipus > Família, + Tipus de Producte) ──────────────
          Gestió unificada i arrossegable de les mateixes dades que les 3
          pestanyes següents (Tipus d'Elements / Família d'Elements / Tipus de
          Producte) — totes tres es mantenen igual per si es prefereix editar
          des d'una taula plana. */}
      {activeTab === 'arbre-elements' && (
        <AdminElementTree showInactive={showInactive} />
      )}

      {/* ── Element Types Tab ──────────────────────────────────────────────────── */}
      {activeTab === 'elements' && (
        <div className="space-y-4">
          {etError && (
            <div className="rounded border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-700 flex justify-between">
              {etError}
              <button onClick={() => setEtError(null)}><X className="h-4 w-4" /></button>
            </div>
          )}

          <div className="rounded-xl border bg-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground w-8">ID</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground w-32">Codi</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground">Nom</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground">Descripció</th>
                  <th className="px-4 py-2 text-center text-xs font-semibold uppercase text-muted-foreground w-20">Estat</th>
                  <th className="px-4 py-2 w-20"></th>
                </tr>
              </thead>
              <tbody>
                {etLoading ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">Carregant...</td></tr>
                ) : visibleET.length === 0 && !etAdding ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">Sense resultats</td></tr>
                ) : (
                  visibleET.map((item) => (
                    <tr key={item.id} className={`border-b last:border-0 ${!item.isActive ? 'opacity-50' : ''}`}>
                      {etEditId === item.id ? (
                        <>
                          <td className="px-4 py-2 text-muted-foreground">{item.id}</td>
                          <td className="px-4 py-2"><InlineInput value={etEditForm.code} onChange={(v) => setEtEditForm(f => ({ ...f, code: v }))} placeholder="CODI" /></td>
                          <td className="px-4 py-2"><InlineInput value={etEditForm.name} onChange={(v) => setEtEditForm(f => ({ ...f, name: v }))} placeholder="Nom" /></td>
                          <td className="px-4 py-2"><InlineInput value={etEditForm.description} onChange={(v) => setEtEditForm(f => ({ ...f, description: v }))} placeholder="Descripció" /></td>
                          <td className="px-4 py-2"></td>
                          <td className="px-4 py-2">
                            <div className="flex gap-1 justify-end">
                              <button onClick={etSaveEdit} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                              <button onClick={() => setEtEditId(null)} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-4 py-2 text-muted-foreground">{item.id}</td>
                          <td className="px-4 py-2 font-mono font-medium">{item.code}</td>
                          <td className="px-4 py-2 font-medium">{item.name}</td>
                          <td className="px-4 py-2 text-muted-foreground">{item.description || '-'}</td>
                          <td className="px-4 py-2 text-center">
                            <button onClick={() => etToggleActive(item)} title={item.isActive ? 'Desactivar' : 'Activar'}>
                              {item.isActive
                                ? <ToggleRight className="h-5 w-5 text-green-600 mx-auto" />
                                : <ToggleLeft className="h-5 w-5 text-muted-foreground mx-auto" />}
                            </button>
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex gap-1 justify-end">
                              <button onClick={() => etStartEdit(item)} className="p-1 hover:bg-muted rounded"><Edit2 className="h-4 w-4" /></button>
                              <button onClick={() => etDelete(item)} className="p-1 hover:bg-red-50 rounded text-red-500"><X className="h-4 w-4" /></button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))
                )}

                {/* New row */}
                {etAdding && (
                  <tr className="border-b bg-blue-50/40">
                    <td className="px-4 py-2 text-muted-foreground text-xs">nou</td>
                    <td className="px-4 py-2"><InlineInput value={etNewForm.code} onChange={(v) => setEtNewForm(f => ({ ...f, code: v }))} placeholder="CODI" /></td>
                    <td className="px-4 py-2"><InlineInput value={etNewForm.name} onChange={(v) => setEtNewForm(f => ({ ...f, name: v }))} placeholder="Nom" /></td>
                    <td className="px-4 py-2"><InlineInput value={etNewForm.description} onChange={(v) => setEtNewForm(f => ({ ...f, description: v }))} placeholder="Descripció (opcional)" /></td>
                    <td className="px-4 py-2"></td>
                    <td className="px-4 py-2">
                      <div className="flex gap-1 justify-end">
                        <button onClick={etSaveNew} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                        <button onClick={() => { setEtAdding(false); setEtNewForm(emptyET()); }} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {!etAdding && (
            <Button size="sm" variant="outline" onClick={() => { setEtAdding(true); setEtEditId(null); }}>
              <Plus className="h-4 w-4 mr-1" />
              Nou tipus d'element
            </Button>
          )}
        </div>
      )}

      {/* ── Material Types Tab ────────────────────────────────────────────────── */}
      {activeTab === 'materials' && (
        <div className="space-y-4">
          {mtError && (
            <div className="rounded border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-700 flex justify-between">
              {mtError}
              <button onClick={() => setMtError(null)}><X className="h-4 w-4" /></button>
            </div>
          )}

          <div className="rounded-xl border bg-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="px-2 py-2 w-6"></th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground w-36">Codi</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground">Nom</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground w-36">{"Tipus d'Elements"}</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground">Descripció</th>
                  <th className="px-4 py-2 text-center text-xs font-semibold uppercase text-muted-foreground w-20">Estat</th>
                  <th className="px-4 py-2 w-20"></th>
                </tr>
              </thead>
              <tbody>
                {mtLoading ? (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">Carregant...</td></tr>
                ) : visibleMT.length === 0 && !mtAdding ? (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">Sense resultats</td></tr>
                ) : (
                  visibleMT.map((item) => (
                    <tr
                      key={item.id}
                      draggable={mtEditId !== item.id}
                      onDragStart={() => mtDragStart(item.id)}
                      onDragOver={(e) => mtDragOver(e, item.id)}
                      onDrop={() => mtDrop(item.id)}
                      onDragEnd={mtDragEnd}
                      className={`border-b last:border-0 transition-colors ${!item.isActive ? 'opacity-50' : ''} ${mtDragOverId === item.id && mtDragId !== item.id ? 'bg-blue-50 border-t-2 border-t-blue-400' : ''}`}
                    >
                      {mtEditId === item.id ? (
                        <>
                          <td className="px-2 py-2 text-slate-300 cursor-default">⠿</td>
                          <td className="px-4 py-2"><InlineInput value={mtEditForm.code} onChange={(v) => setMtEditForm(f => ({ ...f, code: v }))} placeholder="CODI" /></td>
                          <td className="px-4 py-2"><InlineInput value={mtEditForm.name} onChange={(v) => setMtEditForm(f => ({ ...f, name: v }))} placeholder="Nom" /></td>
                          <td className="px-4 py-2">
                            <select
                              value={mtEditForm.elementTypeId}
                              onChange={(e) => setMtEditForm(f => ({ ...f, elementTypeId: e.target.value }))}
                              className="h-7 text-xs border border-input rounded px-2 bg-background w-full"
                            >
                              <option value="">— Selecciona —</option>
                              {elementTypes.filter(et => et.isActive).map(et => (
                                <option key={et.id} value={et.id}>{et.name}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-4 py-2"><InlineInput value={mtEditForm.description} onChange={(v) => setMtEditForm(f => ({ ...f, description: v }))} placeholder="Descripció" /></td>
                          <td className="px-4 py-2"></td>
                          <td className="px-4 py-2">
                            <div className="flex gap-1 justify-end">
                              <button onClick={mtSaveEdit} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                              <button onClick={() => setMtEditId(null)} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-2 py-2 text-slate-300 cursor-grab active:cursor-grabbing select-none">⠿</td>
                          <td className="px-4 py-2 font-mono font-medium">{item.code}</td>
                          <td className="px-4 py-2">{item.name}</td>
                          <td className="px-4 py-2 text-muted-foreground text-xs">{item.elementType?.name || '—'}</td>
                          <td className="px-4 py-2 text-muted-foreground text-xs">{item.description || '-'}</td>
                          <td className="px-4 py-2 text-center">
                            <button onClick={() => mtToggleActive(item)} title={item.isActive ? 'Desactivar' : 'Activar'}>
                              {item.isActive
                                ? <ToggleRight className="h-5 w-5 text-green-600 mx-auto" />
                                : <ToggleLeft className="h-5 w-5 text-muted-foreground mx-auto" />}
                            </button>
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex gap-1 justify-end">
                              <button onClick={() => mtStartEdit(item)} className="p-1 hover:bg-muted rounded"><Edit2 className="h-4 w-4" /></button>
                              <button onClick={() => mtDelete(item)} className="p-1 hover:bg-red-50 rounded text-red-500"><X className="h-4 w-4" /></button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))
                )}

                {/* New row */}
                {mtAdding && (
                  <tr className="border-b bg-blue-50/40">
                    <td className="px-2 py-2"></td>
                    <td className="px-4 py-2"><InlineInput value={mtNewForm.code} onChange={(v) => setMtNewForm(f => ({ ...f, code: v }))} placeholder="CODI" /></td>
                    <td className="px-4 py-2"><InlineInput value={mtNewForm.name} onChange={(v) => setMtNewForm(f => ({ ...f, name: v }))} placeholder="Nom" /></td>
                    <td className="px-4 py-2">
                      <select
                        value={mtNewForm.elementTypeId}
                        onChange={(e) => setMtNewForm(f => ({ ...f, elementTypeId: e.target.value }))}
                        className="h-7 text-xs border border-input rounded px-2 bg-background w-full"
                      >
                        <option value="">— Selecciona —</option>
                        {elementTypes.filter(et => et.isActive).map(et => (
                          <option key={et.id} value={et.id}>{et.name}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-2"><InlineInput value={mtNewForm.description} onChange={(v) => setMtNewForm(f => ({ ...f, description: v }))} placeholder="Descripció (opcional)" /></td>
                    <td className="px-4 py-2"></td>
                    <td className="px-4 py-2">
                      <div className="flex gap-1 justify-end">
                        <button onClick={mtSaveNew} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                        <button onClick={() => { setMtAdding(false); setMtNewForm(emptyMT()); }} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {!mtAdding && (
            <Button size="sm" variant="outline" onClick={() => { setMtAdding(true); setMtEditId(null); }}>
              <Plus className="h-4 w-4 mr-1" />
              Nou tipus de material
            </Button>
          )}
        </div>
      )}

      {/* ── Node Types Tab ────────────────────────────────────────────────────── */}
      {activeTab === 'nodes' && (
        <div className="space-y-4">
          {ntError && (
            <div className="rounded border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-700 flex justify-between">
              {ntError}
              <button onClick={() => setNtError(null)}><X className="h-4 w-4" /></button>
            </div>
          )}

          <div className="rounded-xl border bg-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground w-32">Codi</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground">Nom</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground w-28">Color badge</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground">Descripció</th>
                  <th className="px-4 py-2 text-center text-xs font-semibold uppercase text-muted-foreground w-20">Estat</th>
                  <th className="px-4 py-2 w-20"></th>
                </tr>
              </thead>
              <tbody>
                {ntLoading ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">Carregant...</td></tr>
                ) : visibleNT.length === 0 && !ntAdding ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">Sense resultats</td></tr>
                ) : (
                  visibleNT.map((item) => {
                    const badge = BADGE_COLORS[item.badgeColor] ?? BADGE_COLORS.slate;
                    return (
                      <tr key={item.code} className={`border-b last:border-0 ${!item.isActive ? 'opacity-50' : ''}`}>
                        {ntEditCode === item.code ? (
                          <>
                            <td className="px-4 py-2 font-mono text-muted-foreground">{item.code}</td>
                            <td className="px-4 py-2"><InlineInput value={ntEditForm.name} onChange={(v) => setNtEditForm(f => ({ ...f, name: v }))} placeholder="Nom" /></td>
                            <td className="px-4 py-2">
                              <select
                                value={ntEditForm.badgeColor}
                                onChange={(e) => setNtEditForm(f => ({ ...f, badgeColor: e.target.value }))}
                                className="h-7 text-xs border border-input rounded px-2 bg-background w-full"
                              >
                                {Object.entries(BADGE_COLORS).map(([key, { label }]) => (
                                  <option key={key} value={key}>{label}</option>
                                ))}
                              </select>
                            </td>
                            <td className="px-4 py-2"><InlineInput value={ntEditForm.description} onChange={(v) => setNtEditForm(f => ({ ...f, description: v }))} placeholder="Descripció" /></td>
                            <td className="px-4 py-2"></td>
                            <td className="px-4 py-2">
                              <div className="flex gap-1 justify-end">
                                <button onClick={ntSaveEdit} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                                <button onClick={() => setNtEditCode(null)} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
                              </div>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-4 py-2 font-mono font-semibold">{item.code}</td>
                            <td className="px-4 py-2 font-medium">{item.name}</td>
                            <td className="px-4 py-2">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${badge.cls}`}>{badge.label}</span>
                            </td>
                            <td className="px-4 py-2 text-muted-foreground text-xs">{item.description || '-'}</td>
                            <td className="px-4 py-2 text-center">
                              <button onClick={() => ntToggleActive(item)} title={item.isActive ? 'Desactivar' : 'Activar'}>
                                {item.isActive
                                  ? <ToggleRight className="h-5 w-5 text-green-600 mx-auto" />
                                  : <ToggleLeft className="h-5 w-5 text-muted-foreground mx-auto" />}
                              </button>
                            </td>
                            <td className="px-4 py-2">
                              <div className="flex gap-1 justify-end">
                                <button onClick={() => ntStartEdit(item)} className="p-1 hover:bg-muted rounded"><Edit2 className="h-4 w-4" /></button>
                                <button onClick={() => ntDelete(item)} className="p-1 hover:bg-red-50 rounded text-red-500"><X className="h-4 w-4" /></button>
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })
                )}

                {/* New row */}
                {ntAdding && (
                  <tr className="border-b bg-blue-50/40">
                    <td className="px-4 py-2"><InlineInput value={ntNewForm.code} onChange={(v) => setNtNewForm(f => ({ ...f, code: v }))} placeholder="CODI" /></td>
                    <td className="px-4 py-2"><InlineInput value={ntNewForm.name} onChange={(v) => setNtNewForm(f => ({ ...f, name: v }))} placeholder="Nom" /></td>
                    <td className="px-4 py-2">
                      <select
                        value={ntNewForm.badgeColor}
                        onChange={(e) => setNtNewForm(f => ({ ...f, badgeColor: e.target.value }))}
                        className="h-7 text-xs border border-input rounded px-2 bg-background w-full"
                      >
                        {Object.entries(BADGE_COLORS).map(([key, { label }]) => (
                          <option key={key} value={key}>{label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-2"><InlineInput value={ntNewForm.description} onChange={(v) => setNtNewForm(f => ({ ...f, description: v }))} placeholder="Descripció (opcional)" /></td>
                    <td className="px-4 py-2"></td>
                    <td className="px-4 py-2">
                      <div className="flex gap-1 justify-end">
                        <button onClick={ntSaveNew} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                        <button onClick={() => { setNtAdding(false); setNtNewForm(emptyNT()); }} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {!ntAdding && (
            <Button size="sm" variant="outline" onClick={() => { setNtAdding(true); setNtEditCode(null); }}>
              <Plus className="h-4 w-4 mr-1" />
              Nou tipus d'escandall
            </Button>
          )}
        </div>
      )}

      {/* ── Activitats Tab ────────────────────────────────────────────────────── */}
      {activeTab === 'activitats' && (
        <div className="space-y-4">
          {acError && (
            <div className="rounded border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-700 flex justify-between">
              {acError}
              <button onClick={() => setAcError(null)}><X className="h-4 w-4" /></button>
            </div>
          )}

          <div className="rounded-xl border bg-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="px-2 py-2 w-6"></th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground w-28">Codi</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground">Nom</th>
                  <th className="px-4 py-2 text-center text-xs font-semibold uppercase text-muted-foreground w-20">Estat</th>
                  <th className="px-4 py-2 w-24"></th>
                </tr>
              </thead>
              <tbody>
                {acLoading ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Carregant...</td></tr>
                ) : activitats.length === 0 && !acAdding ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Sense resultats</td></tr>
                ) : (
                  activitats.map((item) => (
                    <tr
                      key={item.id}
                      draggable={acEditId !== item.id}
                      onDragStart={() => acDragStart(item.id)}
                      onDragOver={(e) => acDragOver(e, item.id)}
                      onDrop={() => acDrop(item.id)}
                      onDragEnd={acDragEnd}
                      className={`border-b last:border-0 transition-colors ${!item.isActive ? 'opacity-50' : ''} ${acDragOverId === item.id && acDragId !== item.id ? 'bg-blue-50 border-t-2 border-t-blue-400' : ''}`}
                    >
                      {acEditId === item.id ? (
                        <>
                          <td className="px-2 py-2 text-slate-300 cursor-default">⠿</td>
                          <td className="px-4 py-2">
                            <InlineInput value={acEditForm.code} onChange={(v) => setAcEditForm(f => ({ ...f, code: v }))} placeholder="Codi" />
                          </td>
                          <td className="px-4 py-2">
                            <InlineInput value={acEditForm.name} onChange={(v) => setAcEditForm(f => ({ ...f, name: v }))} placeholder="Nom" />
                          </td>
                          <td className="px-4 py-2"></td>
                          <td className="px-4 py-2">
                            <div className="flex gap-1 justify-end">
                              <button onClick={acSaveEdit} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                              <button onClick={() => setAcEditId(null)} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-2 py-2 text-slate-300 cursor-grab active:cursor-grabbing select-none">⠿</td>
                          <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{item.code || '—'}</td>
                          <td className="px-4 py-2 font-medium">{item.name}</td>
                          <td className="px-4 py-2 text-center">
                            <button onClick={() => acToggleActive(item)} title={item.isActive ? 'Desactivar' : 'Activar'}>
                              {item.isActive
                                ? <ToggleRight className="h-5 w-5 text-green-500" />
                                : <ToggleLeft className="h-5 w-5 text-muted-foreground" />}
                            </button>
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex gap-1 justify-end">
                              <button onClick={() => acStartEdit(item)} className="p-1 hover:bg-muted rounded"><Edit2 className="h-4 w-4" /></button>
                              <button onClick={() => acDelete(item)} className="p-1 hover:bg-red-50 rounded text-red-500"><X className="h-4 w-4" /></button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))
                )}
                {acAdding && (
                  <tr className="border-b bg-muted/30">
                    <td className="px-2 py-2"></td>
                    <td className="px-4 py-2">
                      <InlineInput value={acNewForm.code} onChange={(v) => setAcNewForm(f => ({ ...f, code: v }))} placeholder="Codi" />
                    </td>
                    <td className="px-4 py-2">
                      <InlineInput value={acNewForm.name} onChange={(v) => setAcNewForm(f => ({ ...f, name: v }))} placeholder="Nom de l'activitat" />
                    </td>
                    <td className="px-4 py-2"></td>
                    <td className="px-4 py-2">
                      <div className="flex gap-1 justify-end">
                        <button onClick={acSaveNew} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                        <button onClick={() => { setAcAdding(false); setAcNewForm(emptyAC()); }} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {!acAdding && (
            <Button size="sm" variant="outline" onClick={() => { setAcAdding(true); setAcEditId(null); }}>
              <Plus className="h-4 w-4 mr-1" />
              Nova activitat
            </Button>
          )}
        </div>
      )}

      {/* ── Països Tab ────────────────────────────────────────────────────────── */}
      {activeTab === 'paisos' && (
        <div className="space-y-4">
          {psError && (
            <div className="rounded border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-700 flex justify-between">
              {psError}
              <button onClick={() => setPsError(null)}><X className="h-4 w-4" /></button>
            </div>
          )}
          <div className="rounded-xl border bg-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="px-2 py-2 w-6"></th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground">Nom</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground w-24">Codi</th>
                  <th className="px-4 py-2 text-center text-xs font-semibold uppercase text-muted-foreground w-20">Estat</th>
                  <th className="px-4 py-2 w-24"></th>
                </tr>
              </thead>
              <tbody>
                {psLoading ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Carregant...</td></tr>
                ) : paisos.length === 0 && !psAdding ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Sense resultats</td></tr>
                ) : (
                  paisos.map((item) => (
                    <tr
                      key={item.id}
                      draggable={psEditId !== item.id}
                      onDragStart={() => psDragStart(item.id)}
                      onDragOver={(e) => psDragOver(e, item.id)}
                      onDrop={() => psDrop(item.id)}
                      onDragEnd={psDragEnd}
                      className={`border-b last:border-0 transition-colors ${!item.isActive ? 'opacity-50' : ''} ${psDragOverId === item.id && psDragId !== item.id ? 'bg-blue-50 border-t-2 border-t-blue-400' : ''}`}
                    >
                      {psEditId === item.id ? (
                        <>
                          <td className="px-2 py-2 text-slate-300 cursor-default">⠿</td>
                          <td className="px-4 py-2"><InlineInput value={psEditForm.name} onChange={(v) => setPsEditForm(f => ({ ...f, name: v }))} placeholder="Nom del país" /></td>
                          <td className="px-4 py-2"><InlineInput value={psEditForm.code} onChange={(v) => setPsEditForm(f => ({ ...f, code: v }))} placeholder="ISO" className="w-20" /></td>
                          <td className="px-4 py-2"></td>
                          <td className="px-4 py-2">
                            <div className="flex gap-1 justify-end">
                              <button onClick={psSaveEdit} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                              <button onClick={() => setPsEditId(null)} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-2 py-2 text-slate-300 cursor-grab active:cursor-grabbing select-none">⠿</td>
                          <td className="px-4 py-2 font-medium">{item.name}</td>
                          <td className="px-4 py-2 text-muted-foreground">{item.code || '—'}</td>
                          <td className="px-4 py-2 text-center">
                            <button onClick={() => psToggleActive(item)} title={item.isActive ? 'Desactivar' : 'Activar'}>
                              {item.isActive
                                ? <ToggleRight className="h-5 w-5 text-green-500" />
                                : <ToggleLeft className="h-5 w-5 text-muted-foreground" />}
                            </button>
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex gap-1 justify-end">
                              <button onClick={() => psStartEdit(item)} className="p-1 hover:bg-muted rounded"><Edit2 className="h-4 w-4" /></button>
                              <button onClick={() => psDelete(item)} className="p-1 hover:bg-red-50 rounded text-red-500"><X className="h-4 w-4" /></button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))
                )}
                {psAdding && (
                  <tr className="border-b bg-muted/30">
                    <td className="px-2 py-2"></td>
                    <td className="px-4 py-2"><InlineInput value={psNewForm.name} onChange={(v) => setPsNewForm(f => ({ ...f, name: v }))} placeholder="Nom del país" /></td>
                    <td className="px-4 py-2"><InlineInput value={psNewForm.code} onChange={(v) => setPsNewForm(f => ({ ...f, code: v }))} placeholder="ISO" className="w-20" /></td>
                    <td className="px-4 py-2"></td>
                    <td className="px-4 py-2">
                      <div className="flex gap-1 justify-end">
                        <button onClick={psSaveNew} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                        <button onClick={() => { setPsAdding(false); setPsNewForm(emptyPS()); }} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {!psAdding && (
            <Button size="sm" variant="outline" onClick={() => { setPsAdding(true); setPsEditId(null); }}>
              <Plus className="h-4 w-4 mr-1" />
              Nou país
            </Button>
          )}
        </div>
      )}

      {/* ── Mètodes de Pagament Tab ───────────────────────────────────────────── */}
      {activeTab === 'metodes-pagament' && (
        <div className="space-y-4">
          {mpError && (
            <div className="rounded border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-700 flex justify-between">
              {mpError}
              <button onClick={() => setMpError(null)}><X className="h-4 w-4" /></button>
            </div>
          )}
          <div className="rounded-xl border bg-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground">Nom</th>
                  <th className="px-4 py-2 text-center text-xs font-semibold uppercase text-muted-foreground w-20">Estat</th>
                  <th className="px-4 py-2 w-24"></th>
                </tr>
              </thead>
              <tbody>
                {mpLoading ? (
                  <tr><td colSpan={3} className="px-4 py-8 text-center text-muted-foreground">Carregant...</td></tr>
                ) : metodesPagament.length === 0 && !mpAdding ? (
                  <tr><td colSpan={3} className="px-4 py-8 text-center text-muted-foreground">Sense resultats</td></tr>
                ) : (
                  metodesPagament.map((item) => (
                    <tr key={item.id} className={`border-b last:border-0 ${!item.isActive ? 'opacity-50' : ''}`}>
                      {mpEditId === item.id ? (
                        <>
                          <td className="px-4 py-2"><InlineInput value={mpEditForm.name} onChange={(v) => setMpEditForm(f => ({ ...f, name: v }))} placeholder="Nom del mètode" /></td>
                          <td className="px-4 py-2"></td>
                          <td className="px-4 py-2">
                            <div className="flex gap-1 justify-end">
                              <button onClick={mpSaveEdit} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                              <button onClick={() => setMpEditId(null)} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-4 py-2 font-medium">{item.name}</td>
                          <td className="px-4 py-2 text-center">
                            <button onClick={() => mpToggleActive(item)} title={item.isActive ? 'Desactivar' : 'Activar'}>
                              {item.isActive
                                ? <ToggleRight className="h-5 w-5 text-green-500" />
                                : <ToggleLeft className="h-5 w-5 text-muted-foreground" />}
                            </button>
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex gap-1 justify-end">
                              <button onClick={() => mpStartEdit(item)} className="p-1 hover:bg-muted rounded"><Edit2 className="h-4 w-4" /></button>
                              <button onClick={() => mpDelete(item)} className="p-1 hover:bg-red-50 rounded text-red-500"><X className="h-4 w-4" /></button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))
                )}
                {mpAdding && (
                  <tr className="border-b bg-muted/30">
                    <td className="px-4 py-2"><InlineInput value={mpNewForm.name} onChange={(v) => setMpNewForm(f => ({ ...f, name: v }))} placeholder="Nom del mètode" /></td>
                    <td className="px-4 py-2"></td>
                    <td className="px-4 py-2">
                      <div className="flex gap-1 justify-end">
                        <button onClick={mpSaveNew} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                        <button onClick={() => { setMpAdding(false); setMpNewForm(emptyMP()); }} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {!mpAdding && (
            <Button size="sm" variant="outline" onClick={() => { setMpAdding(true); setMpEditId(null); }}>
              <Plus className="h-4 w-4 mr-1" />
              Nou mètode
            </Button>
          )}
        </div>
      )}

      {/* ── Formes de Tramesa Tab ─────────────────────────────────────────────── */}
      {activeTab === 'formes-tramesa' && (
        <div className="space-y-4">
          {ftError && (
            <div className="rounded border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-700 flex justify-between">
              {ftError}
              <button onClick={() => setFtError(null)}><X className="h-4 w-4" /></button>
            </div>
          )}
          <div className="rounded-xl border bg-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="px-2 py-2 w-6"></th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground">Nom</th>
                  <th className="px-4 py-2 text-center text-xs font-semibold uppercase text-muted-foreground w-20">Estat</th>
                  <th className="px-4 py-2 w-24"></th>
                </tr>
              </thead>
              <tbody>
                {ftLoading ? (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">Carregant...</td></tr>
                ) : formesTramesa.length === 0 && !ftAdding ? (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">Sense resultats</td></tr>
                ) : (
                  formesTramesa.map((item) => (
                    <tr
                      key={item.id}
                      draggable={ftEditId !== item.id}
                      onDragStart={() => ftDragStart(item.id)}
                      onDragOver={(e) => ftDragOver(e, item.id)}
                      onDrop={() => ftDrop(item.id)}
                      onDragEnd={ftDragEnd}
                      className={`border-b last:border-0 transition-colors ${!item.isActive ? 'opacity-50' : ''} ${ftDragOverId === item.id && ftDragId !== item.id ? 'bg-blue-50 border-t-2 border-t-blue-400' : ''}`}
                    >
                      {ftEditId === item.id ? (
                        <>
                          <td className="px-2 py-2 text-slate-300 cursor-default">⠿</td>
                          <td className="px-4 py-2"><InlineInput value={ftEditForm.name} onChange={(v) => setFtEditForm(f => ({ ...f, name: v }))} placeholder="Nom de la forma" /></td>
                          <td className="px-4 py-2"></td>
                          <td className="px-4 py-2">
                            <div className="flex gap-1 justify-end">
                              <button onClick={ftSaveEdit} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                              <button onClick={() => setFtEditId(null)} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-2 py-2 text-slate-300 cursor-grab active:cursor-grabbing select-none">⠿</td>
                          <td className="px-4 py-2 font-medium">{item.name}</td>
                          <td className="px-4 py-2 text-center">
                            <button onClick={() => ftToggleActive(item)} title={item.isActive ? 'Desactivar' : 'Activar'}>
                              {item.isActive
                                ? <ToggleRight className="h-5 w-5 text-green-500" />
                                : <ToggleLeft className="h-5 w-5 text-muted-foreground" />}
                            </button>
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex gap-1 justify-end">
                              <button onClick={() => ftStartEdit(item)} className="p-1 hover:bg-muted rounded"><Edit2 className="h-4 w-4" /></button>
                              <button onClick={() => ftDelete(item)} className="p-1 hover:bg-red-50 rounded text-red-500"><X className="h-4 w-4" /></button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))
                )}
                {ftAdding && (
                  <tr className="border-b bg-muted/30">
                    <td className="px-2 py-2"></td>
                    <td className="px-4 py-2"><InlineInput value={ftNewForm.name} onChange={(v) => setFtNewForm(f => ({ ...f, name: v }))} placeholder="Nom de la forma" /></td>
                    <td className="px-4 py-2"></td>
                    <td className="px-4 py-2">
                      <div className="flex gap-1 justify-end">
                        <button onClick={ftSaveNew} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                        <button onClick={() => { setFtAdding(false); setFtNewForm(emptyFT()); }} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {!ftAdding && (
            <Button size="sm" variant="outline" onClick={() => { setFtAdding(true); setFtEditId(null); }}>
              <Plus className="h-4 w-4 mr-1" />
              Nova forma
            </Button>
          )}
        </div>
      )}

      {/* ── Estats CRM (EC/Pressupost) / Versions Pressupost / Idiomes Pressupost ──
          Estats CRM: catàleg unificat (2026-10-19), substitueix els antics
          Estats Pressupost i Estats Expedient Comercial — vegeu EstatsCrmTable.
          Versions/Idiomes Pressupost: catàlegs {code, name, isActive} amb el
          component genèric de reorder per drag&drop. ── */}
      {activeTab === 'estats-crm' && (
        <EstatsCrmTable showInactive={showInactive} />
      )}
      {activeTab === 'versions-pressupost' && (
        <SimpleCatalogTable apiPath="/admin/versions-pressupost" addLabel="Nova versió" showInactive={showInactive} />
      )}
      {activeTab === 'idiomes-pressupost' && (
        <SimpleCatalogTable apiPath="/admin/idiomes-pressupost" addLabel="Nou idioma" showInactive={showInactive} />
      )}

      {/* ── Tipus de Projecte Tab ─────────────────────────────────────────────── */}
      {activeTab === 'tipus-projecte' && (
        <div className="space-y-4">
          {tpError && (
            <div className="rounded border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-700 flex justify-between">
              {tpError}
              <button onClick={() => setTpError(null)}><X className="h-4 w-4" /></button>
            </div>
          )}

          <div className="rounded-xl border bg-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="px-2 py-2 w-6"></th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground w-28">Codi</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground">Nom</th>
                  <th className="px-4 py-2 text-center text-xs font-semibold uppercase text-muted-foreground w-20">Estat</th>
                  <th className="px-4 py-2 w-24"></th>
                </tr>
              </thead>
              <tbody>
                {tpLoading ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Carregant...</td></tr>
                ) : visibleTP.length === 0 && !tpAdding ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Sense resultats</td></tr>
                ) : (
                  visibleTP.map((item) => (
                    <tr
                      key={item.id}
                      draggable={tpEditId !== item.id}
                      onDragStart={() => tpDragStart(item.id)}
                      onDragOver={(e) => tpDragOver(e, item.id)}
                      onDrop={() => tpDrop(item.id)}
                      onDragEnd={tpDragEnd}
                      className={`border-b last:border-0 transition-colors ${!item.isActive ? 'opacity-50' : ''} ${tpDragOverId === item.id && tpDragId !== item.id ? 'bg-blue-50 border-t-2 border-t-blue-400' : ''}`}
                    >
                      {tpEditId === item.id ? (
                        <>
                          <td className="px-2 py-2 text-slate-300 cursor-default">⠿</td>
                          <td className="px-4 py-2 text-muted-foreground font-mono text-xs">{item.code}</td>
                          <td className="px-4 py-2">
                            <InlineInput value={tpEditForm.name} onChange={(v) => setTpEditForm(f => ({ ...f, name: v }))} placeholder="Nom" />
                          </td>
                          <td className="px-4 py-2"></td>
                          <td className="px-4 py-2">
                            <div className="flex gap-1 justify-end">
                              <button onClick={tpSaveEdit} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                              <button onClick={() => setTpEditId(null)} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-2 py-2 text-slate-300 cursor-grab active:cursor-grabbing select-none">⠿</td>
                          <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{item.code || '—'}</td>
                          <td className="px-4 py-2 font-medium">{item.name}</td>
                          <td className="px-4 py-2 text-center">
                            <button onClick={() => tpToggleActive(item)} title={item.isActive ? 'Desactivar' : 'Activar'}>
                              {item.isActive
                                ? <ToggleRight className="h-5 w-5 text-green-500" />
                                : <ToggleLeft className="h-5 w-5 text-muted-foreground" />}
                            </button>
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex gap-1 justify-end">
                              <button onClick={() => tpStartEdit(item)} className="p-1 hover:bg-muted rounded"><Edit2 className="h-4 w-4" /></button>
                              <button onClick={() => tpDelete(item)} className="p-1 hover:bg-red-50 rounded text-red-500"><X className="h-4 w-4" /></button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))
                )}
                {tpAdding && (
                  <tr className="border-b bg-muted/30">
                    <td className="px-2 py-2"></td>
                    <td className="px-4 py-2">
                      <InlineInput value={tpNewForm.code} onChange={(v) => setTpNewForm(f => ({ ...f, code: v }))} placeholder="Codi" />
                    </td>
                    <td className="px-4 py-2">
                      <InlineInput value={tpNewForm.name} onChange={(v) => setTpNewForm(f => ({ ...f, name: v }))} placeholder="Nom del tipus de projecte" />
                    </td>
                    <td className="px-4 py-2"></td>
                    <td className="px-4 py-2">
                      <div className="flex gap-1 justify-end">
                        <button onClick={tpSaveNew} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                        <button onClick={() => { setTpAdding(false); setTpNewForm(emptyTP()); }} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {!tpAdding && (
            <Button size="sm" variant="outline" onClick={() => { setTpAdding(true); setTpEditId(null); }}>
              <Plus className="h-4 w-4 mr-1" />
              Nou tipus de projecte
            </Button>
          )}
        </div>
      )}

      {/* ── Tipus OT Tab ──────────────────────────────────────────────────────── */}
      {activeTab === 'tipus-ot' && (
        <div className="space-y-4">
          {wotError && (
            <div className="rounded border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-700 flex justify-between">
              {wotError}
              <button onClick={() => setWotError(null)}><X className="h-4 w-4" /></button>
            </div>
          )}

          <div className="rounded-xl border bg-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="px-2 py-2 w-6"></th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground w-28">Codi</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground">Nom</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground">Descripció</th>
                  <th className="px-4 py-2 text-center text-xs font-semibold uppercase text-muted-foreground w-20">Estat</th>
                  <th className="px-4 py-2 w-24"></th>
                </tr>
              </thead>
              <tbody>
                {wotLoading ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">Carregant...</td></tr>
                ) : visibleWOT.length === 0 && !wotAdding ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">Sense resultats</td></tr>
                ) : (
                  visibleWOT.map((item) => (
                    <tr
                      key={item.id}
                      draggable={wotEditId !== item.id}
                      onDragStart={() => wotDragStart(item.id)}
                      onDragOver={(e) => wotDragOver(e, item.id)}
                      onDrop={() => wotDrop(item.id)}
                      onDragEnd={wotDragEnd}
                      className={`border-b last:border-0 transition-colors ${!item.isActive ? 'opacity-50' : ''} ${wotDragOverId === item.id && wotDragId !== item.id ? 'bg-blue-50 border-t-2 border-t-blue-400' : ''}`}
                    >
                      {wotEditId === item.id ? (
                        <>
                          <td className="px-2 py-2 text-slate-300 cursor-default">⠿</td>
                          <td className="px-4 py-2 text-muted-foreground font-mono text-xs">{item.code}</td>
                          <td className="px-4 py-2">
                            <InlineInput value={wotEditForm.name} onChange={(v) => setWotEditForm(f => ({ ...f, name: v }))} placeholder="Nom" />
                          </td>
                          <td className="px-4 py-2">
                            <InlineInput value={wotEditForm.description} onChange={(v) => setWotEditForm(f => ({ ...f, description: v }))} placeholder="Descripció" />
                          </td>
                          <td className="px-4 py-2"></td>
                          <td className="px-4 py-2">
                            <div className="flex gap-1 justify-end">
                              <button onClick={wotSaveEdit} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                              <button onClick={() => setWotEditId(null)} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-2 py-2 text-slate-300 cursor-grab active:cursor-grabbing select-none">⠿</td>
                          <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{item.code || '—'}</td>
                          <td className="px-4 py-2 font-medium">{item.name}</td>
                          <td className="px-4 py-2 text-muted-foreground">{item.description || '-'}</td>
                          <td className="px-4 py-2 text-center">
                            <button onClick={() => wotToggleActive(item)} title={item.isActive ? 'Desactivar' : 'Activar'}>
                              {item.isActive
                                ? <ToggleRight className="h-5 w-5 text-green-500" />
                                : <ToggleLeft className="h-5 w-5 text-muted-foreground" />}
                            </button>
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex gap-1 justify-end">
                              <button onClick={() => wotStartEdit(item)} className="p-1 hover:bg-muted rounded"><Edit2 className="h-4 w-4" /></button>
                              <button onClick={() => wotDelete(item)} className="p-1 hover:bg-red-50 rounded text-red-500"><X className="h-4 w-4" /></button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))
                )}
                {wotAdding && (
                  <tr className="border-b bg-muted/30">
                    <td className="px-2 py-2"></td>
                    <td className="px-4 py-2">
                      <InlineInput value={wotNewForm.code} onChange={(v) => setWotNewForm(f => ({ ...f, code: v }))} placeholder="Codi" />
                    </td>
                    <td className="px-4 py-2">
                      <InlineInput value={wotNewForm.name} onChange={(v) => setWotNewForm(f => ({ ...f, name: v }))} placeholder="Nom del tipus d'OT" />
                    </td>
                    <td className="px-4 py-2">
                      <InlineInput value={wotNewForm.description} onChange={(v) => setWotNewForm(f => ({ ...f, description: v }))} placeholder="Descripció (opcional)" />
                    </td>
                    <td className="px-4 py-2"></td>
                    <td className="px-4 py-2">
                      <div className="flex gap-1 justify-end">
                        <button onClick={wotSaveNew} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                        <button onClick={() => { setWotAdding(false); setWotNewForm(emptyWOT()); }} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {!wotAdding && (
            <Button size="sm" variant="outline" onClick={() => { setWotAdding(true); setWotEditId(null); }}>
              <Plus className="h-4 w-4 mr-1" />
              Nou tipus d&apos;OT
            </Button>
          )}
        </div>
      )}

      {/* ── Grup Equipament Tab ───────────────────────────────────────────────── */}
      {activeTab === 'grups-equipament' && (
        <div className="space-y-4">
          {geError && (
            <div className="rounded border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-700 flex justify-between">
              {geError}
              <button onClick={() => setGeError(null)}><X className="h-4 w-4" /></button>
            </div>
          )}

          <div className="rounded-xl border bg-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground w-40">Codi</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground">Nom</th>
                  <th className="px-4 py-2 text-center text-xs font-semibold uppercase text-muted-foreground w-20">Estat</th>
                  <th className="px-4 py-2 w-24"></th>
                </tr>
              </thead>
              <tbody>
                {geLoading ? (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">Carregant...</td></tr>
                ) : visibleGE.length === 0 && !geAdding ? (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">Sense resultats</td></tr>
                ) : (
                  visibleGE.map((item) => (
                    <tr key={item.id} className={`border-b last:border-0 ${!item.isActive ? 'opacity-50' : ''}`}>
                      {geEditId === item.id ? (
                        <>
                          <td className="px-4 py-2 text-muted-foreground font-mono text-xs">{item.code}</td>
                          <td className="px-4 py-2">
                            <InlineInput value={geEditForm.name} onChange={(v) => setGeEditForm(f => ({ ...f, name: v }))} placeholder="Nom" />
                          </td>
                          <td className="px-4 py-2"></td>
                          <td className="px-4 py-2">
                            <div className="flex gap-1 justify-end">
                              <button onClick={geSaveEdit} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                              <button onClick={() => setGeEditId(null)} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{item.code}</td>
                          <td className="px-4 py-2 font-medium">{item.name}</td>
                          <td className="px-4 py-2 text-center">
                            <button onClick={() => geToggleActive(item)} title={item.isActive ? 'Desactivar' : 'Activar'}>
                              {item.isActive
                                ? <ToggleRight className="h-5 w-5 text-green-500" />
                                : <ToggleLeft className="h-5 w-5 text-muted-foreground" />}
                            </button>
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex gap-1 justify-end">
                              <button onClick={() => geStartEdit(item)} className="p-1 hover:bg-muted rounded"><Edit2 className="h-4 w-4" /></button>
                              <button onClick={() => geDelete(item)} className="p-1 hover:bg-red-50 rounded text-red-500"><X className="h-4 w-4" /></button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))
                )}
                {geAdding && (
                  <tr className="border-b bg-muted/30">
                    <td className="px-4 py-2">
                      <InlineInput value={geNewForm.code} onChange={(v) => setGeNewForm(f => ({ ...f, code: v }))} placeholder="Codi" />
                    </td>
                    <td className="px-4 py-2">
                      <InlineInput value={geNewForm.name} onChange={(v) => setGeNewForm(f => ({ ...f, name: v }))} placeholder="Nom del grup" />
                    </td>
                    <td className="px-4 py-2"></td>
                    <td className="px-4 py-2">
                      <div className="flex gap-1 justify-end">
                        <button onClick={geSaveNew} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                        <button onClick={() => { setGeAdding(false); setGeNewForm(emptyGE()); }} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {!geAdding && (
            <Button size="sm" variant="outline" onClick={() => { setGeAdding(true); setGeEditId(null); }}>
              <Plus className="h-4 w-4 mr-1" />
              Nou grup
            </Button>
          )}
        </div>
      )}

      {/* ── Subgrup Equipament Tab ────────────────────────────────────────────── */}
      {activeTab === 'subgrups-equipament' && (
        <div className="space-y-4">
          {sgeError && (
            <div className="rounded border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-700 flex justify-between">
              {sgeError}
              <button onClick={() => setSgeError(null)}><X className="h-4 w-4" /></button>
            </div>
          )}

          <div className="rounded-xl border bg-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground w-40">Codi</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground">Nom</th>
                  <th className="px-4 py-2 text-center text-xs font-semibold uppercase text-muted-foreground w-20">Estat</th>
                  <th className="px-4 py-2 w-24"></th>
                </tr>
              </thead>
              <tbody>
                {sgeLoading ? (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">Carregant...</td></tr>
                ) : visibleSGE.length === 0 && !sgeAdding ? (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">Sense resultats</td></tr>
                ) : (
                  visibleSGE.map((item) => (
                    <tr key={item.id} className={`border-b last:border-0 ${!item.isActive ? 'opacity-50' : ''}`}>
                      {sgeEditId === item.id ? (
                        <>
                          <td className="px-4 py-2 text-muted-foreground font-mono text-xs">{item.code}</td>
                          <td className="px-4 py-2">
                            <InlineInput value={sgeEditForm.name} onChange={(v) => setSgeEditForm(f => ({ ...f, name: v }))} placeholder="Nom" />
                          </td>
                          <td className="px-4 py-2"></td>
                          <td className="px-4 py-2">
                            <div className="flex gap-1 justify-end">
                              <button onClick={sgeSaveEdit} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                              <button onClick={() => setSgeEditId(null)} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{item.code}</td>
                          <td className="px-4 py-2 font-medium">{item.name}</td>
                          <td className="px-4 py-2 text-center">
                            <button onClick={() => sgeToggleActive(item)} title={item.isActive ? 'Desactivar' : 'Activar'}>
                              {item.isActive
                                ? <ToggleRight className="h-5 w-5 text-green-500" />
                                : <ToggleLeft className="h-5 w-5 text-muted-foreground" />}
                            </button>
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex gap-1 justify-end">
                              <button onClick={() => sgeStartEdit(item)} className="p-1 hover:bg-muted rounded"><Edit2 className="h-4 w-4" /></button>
                              <button onClick={() => sgeDelete(item)} className="p-1 hover:bg-red-50 rounded text-red-500"><X className="h-4 w-4" /></button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))
                )}
                {sgeAdding && (
                  <tr className="border-b bg-muted/30">
                    <td className="px-4 py-2">
                      <InlineInput value={sgeNewForm.code} onChange={(v) => setSgeNewForm(f => ({ ...f, code: v }))} placeholder="Codi" />
                    </td>
                    <td className="px-4 py-2">
                      <InlineInput value={sgeNewForm.name} onChange={(v) => setSgeNewForm(f => ({ ...f, name: v }))} placeholder="Nom del subgrup" />
                    </td>
                    <td className="px-4 py-2"></td>
                    <td className="px-4 py-2">
                      <div className="flex gap-1 justify-end">
                        <button onClick={sgeSaveNew} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                        <button onClick={() => { setSgeAdding(false); setSgeNewForm(emptySGE()); }} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {!sgeAdding && (
            <Button size="sm" variant="outline" onClick={() => { setSgeAdding(true); setSgeEditId(null); }}>
              <Plus className="h-4 w-4 mr-1" />
              Nou subgrup
            </Button>
          )}
        </div>
      )}

      {/* ── Àmbit Equipament Tab ──────────────────────────────────────────────── */}
      {activeTab === 'ambits-equipament' && (
        <div className="space-y-4">
          {aeError && (
            <div className="rounded border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-700 flex justify-between">
              {aeError}
              <button onClick={() => setAeError(null)}><X className="h-4 w-4" /></button>
            </div>
          )}

          <div className="rounded-xl border bg-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground w-40">Codi</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground">Nom</th>
                  <th className="px-4 py-2 text-center text-xs font-semibold uppercase text-muted-foreground w-20">Estat</th>
                  <th className="px-4 py-2 w-24"></th>
                </tr>
              </thead>
              <tbody>
                {aeLoading ? (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">Carregant...</td></tr>
                ) : visibleAE.length === 0 && !aeAdding ? (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">Sense resultats</td></tr>
                ) : (
                  visibleAE.map((item) => (
                    <tr key={item.id} className={`border-b last:border-0 ${!item.isActive ? 'opacity-50' : ''}`}>
                      {aeEditId === item.id ? (
                        <>
                          <td className="px-4 py-2 text-muted-foreground font-mono text-xs">{item.code}</td>
                          <td className="px-4 py-2">
                            <InlineInput value={aeEditForm.name} onChange={(v) => setAeEditForm(f => ({ ...f, name: v }))} placeholder="Nom" />
                          </td>
                          <td className="px-4 py-2"></td>
                          <td className="px-4 py-2">
                            <div className="flex gap-1 justify-end">
                              <button onClick={aeSaveEdit} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                              <button onClick={() => setAeEditId(null)} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{item.code}</td>
                          <td className="px-4 py-2 font-medium">{item.name}</td>
                          <td className="px-4 py-2 text-center">
                            <button onClick={() => aeToggleActive(item)} title={item.isActive ? 'Desactivar' : 'Activar'}>
                              {item.isActive
                                ? <ToggleRight className="h-5 w-5 text-green-500" />
                                : <ToggleLeft className="h-5 w-5 text-muted-foreground" />}
                            </button>
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex gap-1 justify-end">
                              <button onClick={() => aeStartEdit(item)} className="p-1 hover:bg-muted rounded"><Edit2 className="h-4 w-4" /></button>
                              <button onClick={() => aeDelete(item)} className="p-1 hover:bg-red-50 rounded text-red-500"><X className="h-4 w-4" /></button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))
                )}
                {aeAdding && (
                  <tr className="border-b bg-muted/30">
                    <td className="px-4 py-2">
                      <InlineInput value={aeNewForm.code} onChange={(v) => setAeNewForm(f => ({ ...f, code: v }))} placeholder="Codi" />
                    </td>
                    <td className="px-4 py-2">
                      <InlineInput value={aeNewForm.name} onChange={(v) => setAeNewForm(f => ({ ...f, name: v }))} placeholder="Nom de l'àmbit" />
                    </td>
                    <td className="px-4 py-2"></td>
                    <td className="px-4 py-2">
                      <div className="flex gap-1 justify-end">
                        <button onClick={aeSaveNew} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                        <button onClick={() => { setAeAdding(false); setAeNewForm(emptyAE()); }} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {!aeAdding && (
            <Button size="sm" variant="outline" onClick={() => { setAeAdding(true); setAeEditId(null); }}>
              <Plus className="h-4 w-4 mr-1" />
              Nou àmbit
            </Button>
          )}
        </div>
      )}

      {/* ── Grups Expedient Comercial Tab ────────────────────────────────────── */}
      {activeTab === 'grups-ec' && (
        <div className="space-y-4">
          {gecError && (
            <div className="rounded border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-700 flex justify-between">
              {gecError}
              <button onClick={() => setGecError(null)}><X className="h-4 w-4" /></button>
            </div>
          )}

          <div className="rounded-xl border bg-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground w-28">Codi</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground">Nom</th>
                  <th className="px-4 py-2 text-center text-xs font-semibold uppercase text-muted-foreground w-20">Estat</th>
                  <th className="px-4 py-2 w-24"></th>
                </tr>
              </thead>
              <tbody>
                {gecLoading ? (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">Carregant...</td></tr>
                ) : visibleGEC.length === 0 && !gecAdding ? (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">Sense resultats</td></tr>
                ) : (
                  visibleGEC.map((item) => (
                    <tr key={item.id} className={`border-b last:border-0 ${!item.isActive ? 'opacity-50' : ''}`}>
                      {gecEditId === item.id ? (
                        <>
                          <td className="px-4 py-2 text-muted-foreground font-mono text-xs">{item.code}</td>
                          <td className="px-4 py-2">
                            <InlineInput value={gecEditForm.name} onChange={(v) => setGecEditForm(f => ({ ...f, name: v }))} placeholder="Nom" />
                          </td>
                          <td className="px-4 py-2"></td>
                          <td className="px-4 py-2">
                            <div className="flex gap-1 justify-end">
                              <button onClick={gecSaveEdit} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                              <button onClick={() => setGecEditId(null)} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{item.code}</td>
                          <td className="px-4 py-2 font-medium">{item.name}</td>
                          <td className="px-4 py-2 text-center">
                            <button onClick={() => gecToggleActive(item)} title={item.isActive ? 'Desactivar' : 'Activar'}>
                              {item.isActive
                                ? <ToggleRight className="h-5 w-5 text-green-500" />
                                : <ToggleLeft className="h-5 w-5 text-muted-foreground" />}
                            </button>
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex gap-1 justify-end">
                              <button onClick={() => gecStartEdit(item)} className="p-1 hover:bg-muted rounded"><Edit2 className="h-4 w-4" /></button>
                              <button onClick={() => gecDelete(item)} className="p-1 hover:bg-red-50 rounded text-red-500"><X className="h-4 w-4" /></button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))
                )}
                {gecAdding && (
                  <tr className="border-b bg-muted/30">
                    <td className="px-4 py-2">
                      <InlineInput value={gecNewForm.code} onChange={(v) => setGecNewForm(f => ({ ...f, code: v }))} placeholder="Codi" />
                    </td>
                    <td className="px-4 py-2">
                      <InlineInput value={gecNewForm.name} onChange={(v) => setGecNewForm(f => ({ ...f, name: v }))} placeholder="Nom de el grup" />
                    </td>
                    <td className="px-4 py-2"></td>
                    <td className="px-4 py-2">
                      <div className="flex gap-1 justify-end">
                        <button onClick={gecSaveNew} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                        <button onClick={() => { setGecAdding(false); setGecNewForm(emptyGEC()); }} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {!gecAdding && (
            <Button size="sm" variant="outline" onClick={() => { setGecAdding(true); setGecEditId(null); }}>
              <Plus className="h-4 w-4 mr-1" />
              Nou producte
            </Button>
          )}
        </div>
      )}

      {/* ── Naturalesa Client Tab ─────────────────────────────────────────────── */}
      {activeTab === 'naturalesa-client' && (
        <div className="space-y-4">
          {ncError && (
            <div className="rounded border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-700 flex justify-between">
              {ncError}
              <button onClick={() => setNcError(null)}><X className="h-4 w-4" /></button>
            </div>
          )}

          <div className="rounded-xl border bg-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="px-2 py-2 w-6"></th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground w-40">Codi</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground">Nom</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground">Descripció</th>
                  <th className="px-4 py-2 text-center text-xs font-semibold uppercase text-muted-foreground w-20">Estat</th>
                  <th className="px-4 py-2 w-16"></th>
                </tr>
              </thead>
              <tbody>
                {ncLoading ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">Carregant...</td></tr>
                ) : visibleNC.length === 0 && !ncAdding ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">Sense resultats</td></tr>
                ) : (
                  visibleNC.map((item) => (
                    <tr
                      key={item.id}
                      draggable={ncEditId !== item.id}
                      onDragStart={() => ncDragStart(item.id)}
                      onDragOver={(e) => ncDragOver(e, item.id)}
                      onDrop={() => ncDrop(item.id)}
                      onDragEnd={ncDragEnd}
                      className={`border-b last:border-0 transition-colors ${!item.isActive ? 'opacity-50' : ''} ${ncDragOverId === item.id && ncDragId !== item.id ? 'bg-blue-50 border-t-2 border-t-blue-400' : ''}`}
                    >
                      {ncEditId === item.id ? (
                        <>
                          <td className="px-2 py-2 text-slate-300 cursor-default">⠿</td>
                          <td className="px-4 py-2"><InlineInput value={ncEditForm.code} onChange={(v) => setNcEditForm(f => ({ ...f, code: v }))} placeholder="CODI" /></td>
                          <td className="px-4 py-2"><InlineInput value={ncEditForm.name} onChange={(v) => setNcEditForm(f => ({ ...f, name: v }))} placeholder="Nom" /></td>
                          <td className="px-4 py-2"><InlineInput value={ncEditForm.description} onChange={(v) => setNcEditForm(f => ({ ...f, description: v }))} placeholder="Descripció" /></td>
                          <td className="px-4 py-2"></td>
                          <td className="px-4 py-2">
                            <div className="flex gap-1 justify-end">
                              <button onClick={ncSaveEdit} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                              <button onClick={() => setNcEditId(null)} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-2 py-2 text-slate-300 cursor-grab active:cursor-grabbing select-none">⠿</td>
                          <td className="px-4 py-2 font-mono font-medium">{item.code}</td>
                          <td className="px-4 py-2 font-medium">{item.name}</td>
                          <td className="px-4 py-2 text-muted-foreground">{item.description || '-'}</td>
                          <td className="px-4 py-2 text-center">
                            <button onClick={() => ncToggleActive(item)} title={item.isActive ? 'Desactivar' : 'Activar'}>
                              {item.isActive
                                ? <ToggleRight className="h-5 w-5 text-green-600 mx-auto" />
                                : <ToggleLeft className="h-5 w-5 text-muted-foreground mx-auto" />}
                            </button>
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex gap-1 justify-end">
                              <button onClick={() => ncStartEdit(item)} className="p-1 hover:bg-muted rounded"><Edit2 className="h-4 w-4" /></button>
                              <button onClick={() => ncDelete(item)} className="p-1 hover:bg-red-50 rounded text-red-500"><X className="h-4 w-4" /></button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))
                )}
                {ncAdding && (
                  <tr className="border-b bg-blue-50/40">
                    <td className="px-2 py-2"></td>
                    <td className="px-4 py-2"><InlineInput value={ncNewForm.code} onChange={(v) => setNcNewForm(f => ({ ...f, code: v }))} placeholder="CODI" /></td>
                    <td className="px-4 py-2"><InlineInput value={ncNewForm.name} onChange={(v) => setNcNewForm(f => ({ ...f, name: v }))} placeholder="Nom" /></td>
                    <td className="px-4 py-2"><InlineInput value={ncNewForm.description} onChange={(v) => setNcNewForm(f => ({ ...f, description: v }))} placeholder="Descripció (opcional)" /></td>
                    <td className="px-4 py-2"></td>
                    <td className="px-4 py-2">
                      <div className="flex gap-1 justify-end">
                        <button onClick={ncSaveNew} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                        <button onClick={() => { setNcAdding(false); setNcNewForm(emptyNC()); }} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {!ncAdding && (
            <Button size="sm" variant="outline" onClick={() => { setNcAdding(true); setNcEditId(null); }}>
              <Plus className="h-4 w-4 mr-1" />
              Nova naturalesa
            </Button>
          )}
        </div>
      )}

      {/* ── Rol Client Tab ────────────────────────────────────────────────────── */}
      {activeTab === 'rol-client' && (
        <div className="space-y-4">
          {rcError && (
            <div className="rounded border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-700 flex justify-between">
              {rcError}
              <button onClick={() => setRcError(null)}><X className="h-4 w-4" /></button>
            </div>
          )}

          <div className="rounded-xl border bg-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="px-2 py-2 w-6"></th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground w-40">Codi</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground">Nom</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground">Descripció</th>
                  <th className="px-4 py-2 text-center text-xs font-semibold uppercase text-muted-foreground w-20">Estat</th>
                  <th className="px-4 py-2 w-16"></th>
                </tr>
              </thead>
              <tbody>
                {rcLoading ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">Carregant...</td></tr>
                ) : visibleRC.length === 0 && !rcAdding ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">Sense resultats</td></tr>
                ) : (
                  visibleRC.map((item) => (
                    <tr
                      key={item.id}
                      draggable={rcEditId !== item.id}
                      onDragStart={() => rcDragStart(item.id)}
                      onDragOver={(e) => rcDragOver(e, item.id)}
                      onDrop={() => rcDrop(item.id)}
                      onDragEnd={rcDragEnd}
                      className={`border-b last:border-0 transition-colors ${!item.isActive ? 'opacity-50' : ''} ${rcDragOverId === item.id && rcDragId !== item.id ? 'bg-blue-50 border-t-2 border-t-blue-400' : ''}`}
                    >
                      {rcEditId === item.id ? (
                        <>
                          <td className="px-2 py-2 text-slate-300 cursor-default">⠿</td>
                          <td className="px-4 py-2"><InlineInput value={rcEditForm.code} onChange={(v) => setRcEditForm(f => ({ ...f, code: v }))} placeholder="CODI" /></td>
                          <td className="px-4 py-2"><InlineInput value={rcEditForm.name} onChange={(v) => setRcEditForm(f => ({ ...f, name: v }))} placeholder="Nom" /></td>
                          <td className="px-4 py-2"><InlineInput value={rcEditForm.description} onChange={(v) => setRcEditForm(f => ({ ...f, description: v }))} placeholder="Descripció" /></td>
                          <td className="px-4 py-2"></td>
                          <td className="px-4 py-2">
                            <div className="flex gap-1 justify-end">
                              <button onClick={rcSaveEdit} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                              <button onClick={() => setRcEditId(null)} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-2 py-2 text-slate-300 cursor-grab active:cursor-grabbing select-none">⠿</td>
                          <td className="px-4 py-2 font-mono font-medium">{item.code}</td>
                          <td className="px-4 py-2 font-medium">{item.name}</td>
                          <td className="px-4 py-2 text-muted-foreground">{item.description || '-'}</td>
                          <td className="px-4 py-2 text-center">
                            <button onClick={() => rcToggleActive(item)} title={item.isActive ? 'Desactivar' : 'Activar'}>
                              {item.isActive
                                ? <ToggleRight className="h-5 w-5 text-green-600 mx-auto" />
                                : <ToggleLeft className="h-5 w-5 text-muted-foreground mx-auto" />}
                            </button>
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex gap-1 justify-end">
                              <button onClick={() => rcStartEdit(item)} className="p-1 hover:bg-muted rounded"><Edit2 className="h-4 w-4" /></button>
                              <button onClick={() => rcDelete(item)} className="p-1 hover:bg-red-50 rounded text-red-500"><X className="h-4 w-4" /></button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))
                )}
                {rcAdding && (
                  <tr className="border-b bg-blue-50/40">
                    <td className="px-2 py-2"></td>
                    <td className="px-4 py-2"><InlineInput value={rcNewForm.code} onChange={(v) => setRcNewForm(f => ({ ...f, code: v }))} placeholder="CODI" /></td>
                    <td className="px-4 py-2"><InlineInput value={rcNewForm.name} onChange={(v) => setRcNewForm(f => ({ ...f, name: v }))} placeholder="Nom" /></td>
                    <td className="px-4 py-2"><InlineInput value={rcNewForm.description} onChange={(v) => setRcNewForm(f => ({ ...f, description: v }))} placeholder="Descripció (opcional)" /></td>
                    <td className="px-4 py-2"></td>
                    <td className="px-4 py-2">
                      <div className="flex gap-1 justify-end">
                        <button onClick={rcSaveNew} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                        <button onClick={() => { setRcAdding(false); setRcNewForm(emptyRC()); }} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {!rcAdding && (
            <Button size="sm" variant="outline" onClick={() => { setRcAdding(true); setRcEditId(null); }}>
              <Plus className="h-4 w-4 mr-1" />
              Nou rol
            </Button>
          )}
        </div>
      )}

      {/* ── Estat Comercial Tab ───────────────────────────────────────────────── */}
      {activeTab === 'estats-comercials' && (
        <div className="space-y-4">
          {esError && (
            <div className="rounded border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-700 flex justify-between">
              {esError}
              <button onClick={() => setEsError(null)}><X className="h-4 w-4" /></button>
            </div>
          )}

          <div className="rounded-xl border bg-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="px-2 py-2 w-6"></th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground w-40">Codi</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground">Nom</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground">Descripció</th>
                  <th className="px-4 py-2 text-center text-xs font-semibold uppercase text-muted-foreground w-20">Estat</th>
                  <th className="px-4 py-2 w-16"></th>
                </tr>
              </thead>
              <tbody>
                {esLoading ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">Carregant...</td></tr>
                ) : visibleES.length === 0 && !esAdding ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">Sense resultats</td></tr>
                ) : (
                  visibleES.map((item) => (
                    <tr
                      key={item.id}
                      draggable={esEditId !== item.id}
                      onDragStart={() => esDragStart(item.id)}
                      onDragOver={(e) => esDragOver(e, item.id)}
                      onDrop={() => esDrop(item.id)}
                      onDragEnd={esDragEnd}
                      className={`border-b last:border-0 transition-colors ${!item.isActive ? 'opacity-50' : ''} ${esDragOverId === item.id && esDragId !== item.id ? 'bg-blue-50 border-t-2 border-t-blue-400' : ''}`}
                    >
                      {esEditId === item.id ? (
                        <>
                          <td className="px-2 py-2 text-slate-300 cursor-default">⠿</td>
                          <td className="px-4 py-2"><InlineInput value={esEditForm.code} onChange={(v) => setEsEditForm(f => ({ ...f, code: v }))} placeholder="CODI" /></td>
                          <td className="px-4 py-2"><InlineInput value={esEditForm.name} onChange={(v) => setEsEditForm(f => ({ ...f, name: v }))} placeholder="Nom" /></td>
                          <td className="px-4 py-2"><InlineInput value={esEditForm.description} onChange={(v) => setEsEditForm(f => ({ ...f, description: v }))} placeholder="Descripció" /></td>
                          <td className="px-4 py-2"></td>
                          <td className="px-4 py-2">
                            <div className="flex gap-1 justify-end">
                              <button onClick={esSaveEdit} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                              <button onClick={() => setEsEditId(null)} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-2 py-2 text-slate-300 cursor-grab active:cursor-grabbing select-none">⠿</td>
                          <td className="px-4 py-2 font-mono font-medium">{item.code}</td>
                          <td className="px-4 py-2 font-medium">{item.name}</td>
                          <td className="px-4 py-2 text-muted-foreground">{item.description || '-'}</td>
                          <td className="px-4 py-2 text-center">
                            <button onClick={() => esToggleActive(item)} title={item.isActive ? 'Desactivar' : 'Activar'}>
                              {item.isActive
                                ? <ToggleRight className="h-5 w-5 text-green-600 mx-auto" />
                                : <ToggleLeft className="h-5 w-5 text-muted-foreground mx-auto" />}
                            </button>
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex gap-1 justify-end">
                              <button onClick={() => esStartEdit(item)} className="p-1 hover:bg-muted rounded"><Edit2 className="h-4 w-4" /></button>
                              <button onClick={() => esDelete(item)} className="p-1 hover:bg-red-50 rounded text-red-500"><X className="h-4 w-4" /></button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))
                )}
                {esAdding && (
                  <tr className="border-b bg-blue-50/40">
                    <td className="px-2 py-2"></td>
                    <td className="px-4 py-2"><InlineInput value={esNewForm.code} onChange={(v) => setEsNewForm(f => ({ ...f, code: v }))} placeholder="CODI" /></td>
                    <td className="px-4 py-2"><InlineInput value={esNewForm.name} onChange={(v) => setEsNewForm(f => ({ ...f, name: v }))} placeholder="Nom" /></td>
                    <td className="px-4 py-2"><InlineInput value={esNewForm.description} onChange={(v) => setEsNewForm(f => ({ ...f, description: v }))} placeholder="Descripció (opcional)" /></td>
                    <td className="px-4 py-2"></td>
                    <td className="px-4 py-2">
                      <div className="flex gap-1 justify-end">
                        <button onClick={esSaveNew} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                        <button onClick={() => { setEsAdding(false); setEsNewForm(emptyES()); }} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {!esAdding && (
            <Button size="sm" variant="outline" onClick={() => { setEsAdding(true); setEsEditId(null); }}>
              <Plus className="h-4 w-4 mr-1" />
              Nou estat comercial
            </Button>
          )}
        </div>
      )}

      {/* ── Hores de Personal Tab (reutilitza la pantalla RRHH > Imputació d'Hores) ── */}
      {/* ── Hores de Personal Tab ─────────────────────────────────────────────── */}
      {activeTab === 'hores-personal' && (
        <div className="space-y-4">
          {hpError && (
            <div className="rounded border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-700 flex justify-between">
              {hpError}
              <button onClick={() => setHpError(null)}><X className="h-4 w-4" /></button>
            </div>
          )}

          <div className="rounded-xl border bg-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground w-28">Codi</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground">Nom</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold uppercase text-muted-foreground w-28">€/h</th>
                  <th className="px-4 py-2 text-center text-xs font-semibold uppercase text-muted-foreground w-20">Estat</th>
                  <th className="px-4 py-2 w-24"></th>
                </tr>
              </thead>
              <tbody>
                {hpLoading ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Carregant...</td></tr>
                ) : visibleHP.length === 0 && !hpAdding ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Sense resultats</td></tr>
                ) : (
                  visibleHP.map((item) => (
                    <tr key={item.id} className={`border-b last:border-0 ${!item.isActive ? 'opacity-50' : ''}`}>
                      {hpEditId === item.id ? (
                        <>
                          <td className="px-4 py-2 text-muted-foreground font-mono text-xs">{item.code}</td>
                          <td className="px-4 py-2">
                            <InlineInput value={hpEditForm.name} onChange={(v) => setHpEditForm(f => ({ ...f, name: v }))} placeholder="Nom" />
                          </td>
                          <td className="px-4 py-2">
                            <InlineInput value={hpEditForm.hourlyRate} onChange={(v) => setHpEditForm(f => ({ ...f, hourlyRate: v }))} placeholder="0,00" />
                          </td>
                          <td className="px-4 py-2"></td>
                          <td className="px-4 py-2">
                            <div className="flex gap-1 justify-end">
                              <button onClick={hpSaveEdit} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                              <button onClick={() => setHpEditId(null)} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{item.code}</td>
                          <td className="px-4 py-2 font-medium">{item.name}</td>
                          <td className="px-4 py-2 text-right tabular-nums">{Number(item.hourlyRate).toLocaleString('ca-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €/h</td>
                          <td className="px-4 py-2 text-center">
                            <button onClick={() => hpToggleActive(item)} title={item.isActive ? 'Desactivar' : 'Activar'}>
                              {item.isActive
                                ? <ToggleRight className="h-5 w-5 text-green-500" />
                                : <ToggleLeft className="h-5 w-5 text-muted-foreground" />}
                            </button>
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex gap-1 justify-end">
                              <button onClick={() => hpStartEdit(item)} className="p-1 hover:bg-muted rounded"><Edit2 className="h-4 w-4" /></button>
                              <button onClick={() => hpDelete(item)} className="p-1 hover:bg-red-50 rounded text-red-500"><X className="h-4 w-4" /></button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))
                )}
                {hpAdding && (
                  <tr className="border-b bg-muted/30">
                    <td className="px-4 py-2">
                      <InlineInput value={hpNewForm.code} onChange={(v) => setHpNewForm(f => ({ ...f, code: v }))} placeholder="Codi" />
                    </td>
                    <td className="px-4 py-2">
                      <InlineInput value={hpNewForm.name} onChange={(v) => setHpNewForm(f => ({ ...f, name: v }))} placeholder="Nom (ex: Hora de treball fabricació)" />
                    </td>
                    <td className="px-4 py-2">
                      <InlineInput value={hpNewForm.hourlyRate} onChange={(v) => setHpNewForm(f => ({ ...f, hourlyRate: v }))} placeholder="0,00" />
                    </td>
                    <td className="px-4 py-2"></td>
                    <td className="px-4 py-2">
                      <div className="flex gap-1 justify-end">
                        <button onClick={hpSaveNew} className="p-1 hover:bg-green-100 rounded text-green-600"><Check className="h-4 w-4" /></button>
                        <button onClick={() => { setHpAdding(false); setHpNewForm(emptyHP()); }} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {!hpAdding && (
            <Button size="sm" variant="outline" onClick={() => { setHpAdding(true); setHpEditId(null); }}>
              <Plus className="h-4 w-4 mr-1" />
              Nova hora de personal
            </Button>
          )}
        </div>
      )}

      {/* ── Catàlegs RRHH nous (Relació Laboral, Tipus de Contracte, Departament,
          Càrrec, Talla de Pantalons, Talla de Camisa) — abans ENUM fixes a la
          fitxa d'empleat, ara editables aquí amb el component genèric. ── */}
      {activeTab === 'relacio-laboral' && (
        <SimpleCatalogTable apiPath="/catalog/relacio-laboral" addLabel="Nova relació laboral" showInactive={showInactive} />
      )}
      {activeTab === 'tipus-contracte' && (
        <SimpleCatalogTable apiPath="/catalog/tipus-contracte" addLabel="Nou tipus de contracte" showInactive={showInactive} />
      )}
      {activeTab === 'departament' && (
        <SimpleCatalogTable apiPath="/catalog/departament" addLabel="Nou departament" showInactive={showInactive} />
      )}
      {activeTab === 'carrec' && (
        <SimpleCatalogTable apiPath="/catalog/carrec" addLabel="Nou càrrec" showInactive={showInactive} />
      )}
      {activeTab === 'talla-pantalons' && (
        <SimpleCatalogTable apiPath="/catalog/talla-pantalons" addLabel="Nova talla de pantalons" showInactive={showInactive} />
      )}
      {activeTab === 'talla-camisa' && (
        <SimpleCatalogTable apiPath="/catalog/talla-camisa" addLabel="Nova talla de camisa" showInactive={showInactive} />
      )}
    </div>
  );
}
